import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import BusinessSidebar from 'components/ui/BusinessSidebar';
import PanelHeader from 'components/ui/PanelHeader';
import Icon from 'components/AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import { useIsDesktop } from 'hooks/useMediaQuery';
import { getCrmCustomers, getPosProducts, getAllActiveProducts, createPosInvoice, getOpenCashSession, createCrmCustomer, holdPosSale, listHeldPosSales, claimHeldPosSale, releaseClaimedPosSale, consumeClaimedPosSale, discardHeldPosSale, getPointTerminals, getPointTerminalPreference, selectPointTerminal, unlinkPointTerminal, verifyPointTerminal, startPointOauth, getPointConnection, disconnectPointConnection, setupPointTerminal, createPointOrder, getPointOrder, cancelPointOrder } from '../../services/crmService';
import { getEffectivePlanSlug } from '../../services/waBusinessService';
import { canUseFeature } from '../../config/planFeatures';
import CrmThermalTicket from './components/CrmThermalTicket';
import { QuickCustomerModal } from './components/QuickCustomerModal';
import CrmBreadcrumb from 'components/ui/CrmBreadcrumb';
import { formatMoney, fmtMoneyInput, parseMoneyInput } from '../../utils/formatMoney';
import { createDebouncer } from '../../lib/debounce';
import {
  buildPosTerminalDraftKey,
  readPosTerminalDraft,
  writePosTerminalDraft,
  removePosTerminalDraft,
  buildPosTerminalDraftSnapshot,
  isDraftFromToday,
} from '../../lib/posTerminalDraftStorage';
import { printService } from 'lib/printing/printService';
import { buildSaleReceipt } from 'lib/printing/receipts/buildSaleReceipt';
import { buildPrinterConfigKey, readPrinterConfig } from 'lib/printing/printerConfigStorage';
import { buildConnectorConfigKey, readConnectorConfig } from 'lib/printing/connectorConfigStorage';
import { buildPrintTarget, resolvePrintRoute, ROUTE } from 'lib/printing/printRouting';

// CAJA-CIERRE-CONCILIACION-1 -- 'card' genérico se reemplaza por
// debit_card/credit_card para ventas NUEVAS (permite conciliar cada
// terminal por separado en el asistente de cierre de caja). Las ventas
// históricas con payment_method='card' NO se reclasifican -- siguen
// siendo 'card' en crm_payments, solo cambia qué puede elegir el cajero
// de ahora en adelante. Se agrega mercado_pago como medio propio (antes
// cabía forzado dentro de 'other' o 'card').
const PAYMENT_METHODS = [
  { value: 'cash',          label: 'Efectivo',       icon: 'Banknote' },
  { value: 'debit_card',    label: 'Débito',         icon: 'CreditCard' },
  { value: 'credit_card',   label: 'Crédito',        icon: 'CreditCard' },
  { value: 'mercado_pago',  label: 'MP externo (ya cobrado)',   icon: 'Wallet' },
  { value: 'bank_transfer', label: 'Transferencia',  icon: 'ArrowLeftRight' },
  { value: 'check',         label: 'Cheque',         icon: 'BadgeCheck' },
  { value: 'other',         label: 'Otro',           icon: 'MoreHorizontal' },
  { value: 'credit',        label: 'Cta. cte.',      icon: 'BookUser' },
];

const REAL_PAYMENT_METHODS = PAYMENT_METHODS.filter((method) => method.value !== 'credit');

const fmt = (n, currency) => formatMoney(n, currency);

function getProductImageSrc(product) {
  if (product.thumbnail_url) return product.thumbnail_url;
  if (product.card_image_url) return product.card_image_url;
  if (product.image_url) return product.image_url;
  if (Array.isArray(product.images) && product.images.length > 0) {
    const first = product.images[0];
    return typeof first === 'string' ? first : (first?.url || first?.src || null);
  }
  return null;
}

function ProductThumb({ product }) {
  const src = getProductImageSrc(product);
  if (src) {
    return (
      <img
        src={src}
        alt={product.name}
        className="aspect-[4/3] w-full object-contain rounded-xl bg-gray-50"
        loading="lazy"
      />
    );
  }
  const initial = (product.name || '?')[0].toUpperCase();
  return (
    <div className="aspect-[4/3] w-full rounded-xl bg-gradient-to-br from-gray-100 to-gray-50 flex items-center justify-center text-3xl font-black text-gray-300 select-none">
      {initial}
    </div>
  );
}

function ManualItemModal({ onAdd, onClose, currency }) {
  const [name, setName] = useState('');
  const [price, setPrice] = useState('');
  const [qty, setQty] = useState('1');
  const [note, setNote] = useState('');
  const [err, setErr] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    const trimmedName = name.trim();
    if (!trimmedName) { setErr('Ingresa un nombre para el artículo.'); return; }
    const parsedPrice = parseMoneyInput(price);
    if (isNaN(parsedPrice) || parsedPrice < 0) { setErr('Precio inválido.'); return; }
    const parsedQty = Math.max(1, Math.round(+qty) || 1);
    onAdd({ name: trimmedName, unit_price: parsedPrice, quantity: parsedQty, note: note.trim() || null });
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-sm shadow-2xl">
        <div className="flex justify-between items-center px-5 py-4 border-b">
          <h2 className="font-bold text-gray-900 text-base flex items-center gap-2">
            <Icon name="PenLine" size={16} className="text-purple-500" />
            Artículo manual
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100">
            <Icon name="X" size={18} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="px-5 py-4 space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Nombre *</label>
            <input
              autoFocus
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Ej: Servicio técnico, Traslado, etc."
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>
          <div className="flex gap-3">
            <div className="flex-1">
              <label className="block text-xs font-medium text-gray-700 mb-1">Precio unitario *</label>
              <div className="relative">
                <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs pointer-events-none">$</span>
                <input
                  type="text"
                  inputMode="numeric"
                  value={fmtMoneyInput(price)}
                  onChange={e => setPrice(e.target.value.replace(/\D/g, ''))}
                  placeholder="0"
                  className="w-full pl-6 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
            </div>
            <div className="w-24">
              <label className="block text-xs font-medium text-gray-700 mb-1">Cantidad</label>
              <input
                type="number"
                min="1"
                step="1"
                value={qty}
                onChange={e => setQty(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Nota (opcional)</label>
            <input
              type="text"
              value={note}
              onChange={e => setNote(e.target.value)}
              placeholder="Descripción adicional…"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>
          {err && <p className="text-xs text-red-600 bg-red-50 px-3 py-2 rounded-lg">{err}</p>}
          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose} className="flex-1 py-2.5 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm font-medium">
              Cancelar
            </button>
            <button type="submit" className="flex-1 py-2.5 rounded-lg bg-purple-600 hover:bg-purple-700 text-white text-sm font-semibold">
              Agregar al carrito
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}


// ── ErrorBoundary específico del TPV ─────────────────────────────────────────
// Clase separada de ErrorBoundary.jsx para mostrar "Recargar terminal"
// en lugar de redirigir al inicio, preservando el contexto de caja.
class CrmTerminalBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(error, info) {
    console.error('[CrmTerminal] error capturado por boundary:', error, info?.componentStack?.slice(0, 300));
  }
  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <div className="text-center p-8 max-w-sm">
          <div className="text-5xl mb-4" role="img" aria-label="Error">⚠️</div>
          <h2 className="text-xl font-semibold text-neutral-800 mb-2">El terminal encontró un problema</h2>
          <p className="text-neutral-500 text-sm mb-6">
            No se perdió ninguna venta. Recarga para continuar.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-5 rounded-lg transition-colors"
          >
            Recargar terminal
          </button>
        </div>
      </div>
    );
  }
}

function CrmTerminalUI() {
  const navigate = useNavigate();
  const { business } = useAuth();
  const isDesktop = useIsDesktop();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const effectivePlan = getEffectivePlanSlug(
    business?.planSlug,
    business?.planExpiresAt,
    business?.trialExpiresAt
  );
  const hasAccess = canUseFeature(effectivePlan, 'pos');

  // posProducts: solo show_in_pos=true — se muestran en la grilla sin búsqueda
  // allProducts: todos los activos — se cargan en background para búsqueda global
  const [posProducts, setPosProducts]   = useState([]);
  const [allProducts, setAllProducts]   = useState([]);
  const [posLoading,  setPosLoading]    = useState(true);
  const [posFallback, setPosFallback]   = useState(false); // true si migración pos no aplicada
  const [diagVisible, setDiagVisible]   = useState(false);
  const allProductsRef = useRef([]);    // ref para findExactProduct sin re-render
  const customers = useRef([]);
  const [customersDisplay, setCustomersDisplay] = useState([]);
  const [search, setSearch] = useState('');
  const searchDebounceRef = useRef(null);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('');
  const [cart, setCart] = useState([]);
  const [customerId, setCustomerId] = useState('');
  const [discount, setDiscount] = useState('');
  const [payments, setPayments] = useState([{ id: 'payment_1', method: 'cash', amount: '' }]);
  const [splitPaymentMode, setSplitPaymentMode] = useState(false);
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  // POS-HELD-SALES-1: ventas suspendidas deliberadamente por el cajero.
  const [heldSales, setHeldSales] = useState([]);
  const [heldSalesOpen, setHeldSalesOpen] = useState(false);
  const [heldBusy, setHeldBusy] = useState(false);
  // POINT-SMART-2-7: el flujo integrado es deliberadamente separado del
  // medio "Mercado Pago manual". Fase 1 cobra el TOTAL completo por Point;
  // pagos mixtos con Point quedan fuera hasta modelarlos explícitamente.
  const [pointTerminals, setPointTerminals] = useState([]);
  const [pointTerminalId, setPointTerminalId] = useState('');
  const [pointPreference, setPointPreference] = useState(null);
  const [pointConnection, setPointConnection] = useState(null);
  const [pointLoading, setPointLoading] = useState(false);
  const [pointError, setPointError] = useState(null);
  const [pointErrorReason, setPointErrorReason] = useState(null);
  const [pointOperation, setPointOperation] = useState(null);
  // Conserva el último resultado terminal solo para diagnóstico/UX. No es una
  // operación activa y nunca bloquea un cobro nuevo; permite ver cómo terminó
  // el intento anterior sin crear otra order Point.
  const [pointLastResult, setPointLastResult] = useState(null);
  const [pointCanceling, setPointCanceling] = useState(false);
  const pointPollRef = useRef(null);
  const pointCreateKeyRef = useRef(null);
  const pointStorageKey = useMemo(() => business?.id ? `walinka:point-active:${business.id}` : null, [business?.id]);


  // TPV-CORE-3: paso del flujo de checkout -- puramente de interfaz, nunca
  // representa una venta registrada ni se persiste en el draft (ver
  // efecto de restauración más abajo: tras refresh siempre vuelve a
  // 'sale', incluso si el carrito se restaura).
  const [checkoutStep, setCheckoutStep] = useState('sale'); // 'sale' | 'payment'
  // TPV-CORE-1: lock síncrono contra doble submit (busy es un useState, se
  // re-renderiza de forma asíncrona -- un doble click a pocos ms de
  // distancia podría disparar handleRegister dos veces antes de que React
  // comprometa disabled=busy). Es solo UX: la protección autoritativa real
  // es la idempotencia de crm_create_pos_sale en la base de datos.
  const submitLockRef = useRef(false);
  // Idempotency key estable por intento de venta: se genera una sola vez,
  // se reenvía sin cambios en cualquier reintento del MISMO intento (error
  // de red, error del servidor, doble click), y solo se renueva después de
  // una venta completada con éxito o de un reset real del formulario --
  // nunca por un timeout ambiguo antes de saber si la venta se creó.
  const saleIdempotencyKeyRef = useRef(null);
  const getOrCreateSaleIdempotencyKey = () => {
    if (!saleIdempotencyKeyRef.current) {
      saleIdempotencyKeyRef.current = (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function')
        ? crypto.randomUUID()
        : `pos_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    }
    return saleIdempotencyKeyRef.current;
  };

  // TPV-CORE-2: borrador local de la venta en curso -- puramente UI, ver
  // src/lib/posTerminalDraftStorage.js. Nunca crea efectos reales (eso solo
  // lo hace crm_create_pos_sale al completar).
  const draftKey = useMemo(() => buildPosTerminalDraftKey(business?.id), [business?.id]);
  const [draftNotice, setDraftNotice] = useState(null); // 'restored' | { savedAt } (pendiente de decisión) | null
  const [customerRemovedNotice, setCustomerRemovedNotice] = useState(false);
  const [customersLoaded, setCustomersLoaded] = useState(false);
  const restoreCheckedRef = useRef(false); // true una vez que ya se intentó restaurar (o no había nada que restaurar)
  const draftDebouncerRef = useRef(null);
  if (!draftDebouncerRef.current) draftDebouncerRef.current = createDebouncer(400);
  const cartSectionRef = useRef(null);
  const [showManualModal, setShowManualModal] = useState(false);
  const [showNewCustomer, setShowNewCustomer] = useState(false);

  // Ticket modal state — set after successful (or locally-fallback) sale
  const [ticketData, setTicketData] = useState(null);

  const searchRef = useRef(null);
  const printedTicketRef = useRef(null);
  // E3 — claves de idempotencia del conector. `reprintRequestedRef` lo marca
  // "Reimprimir" (clave nueva, la venta ya se imprimió); `printAttemptRef`
  // recuerda el último intento: tras un error el reintento reutiliza la misma
  // clave (el conector solo reenvía una clave en estado `failed`).
  const reprintRequestedRef = useRef(false);
  const printAttemptRef = useRef({ saleKey: null, key: null, failed: false });

  // PRINT-2 — impresión real del ticket vía PrintService/QZ Tray, sin el
  // diálogo de impresión del navegador. La venta ya está guardada
  // (crm_create_pos_sale) antes
  // de que este efecto se dispare: reintentar o fallar acá nunca vuelve a
  // crear la venta, duplicar un pago ni revertir stock -- solo reintenta
  // el envío de los mismos bytes ya renderizados a la impresora.
  // 'idle' | 'missing_printer' | 'printing' | 'success' | 'error'
  const [printStatus, setPrintStatus] = useState('idle');
  const [printErrorMessage, setPrintErrorMessage] = useState(null);

  const printCurrentTicket = useCallback(async () => {
    if (!ticketData) return;
    const printerConfig = readPrinterConfig(buildPrinterConfigKey(business?.id));
    const connectorConfig = readConnectorConfig(buildConnectorConfigKey(business?.id));
    // Con el Walinka Print Connector activo no depende de la impresora QZ.
    if (!printerConfig.printerName) {
      if (resolvePrintRoute(connectorConfig) !== ROUTE.CONNECTOR) {
        setPrintStatus('missing_printer');
        setPrintErrorMessage(null);
        return;
      }
    }
    const saleKey = String(ticketData.sale?.id ?? ticketData.sale?.invoice_number);
    const prevAttempt = printAttemptRef.current;
    const sameSale = prevAttempt.saleKey === saleKey;
    const reprintRequested = reprintRequestedRef.current;
    reprintRequestedRef.current = false;
    let idempotencyKey;
    if (sameSale && prevAttempt.failed) idempotencyKey = prevAttempt.key;
    else if (sameSale && reprintRequested) idempotencyKey = `sale:${saleKey}:reprint:${Date.now()}${Math.random().toString(36).slice(2, 8)}`;
    else idempotencyKey = `sale:${saleKey}:ticket`;
    const target = buildPrintTarget({ connectorConfig, qzPrinterName: printerConfig.printerName, idempotencyKey });
    if (target.error) {
      setPrintStatus('missing_printer');
      setPrintErrorMessage(null);
      return;
    }
    setPrintStatus('printing');
    setPrintErrorMessage(null);
    try {
      const receipt = buildSaleReceipt({
        business,
        ...ticketData,
        paperWidthMm: printerConfig.paperWidthMm,
        autoCut: printerConfig.autoCut,
        printLogo: printerConfig.printLogo,
        imageMode: printerConfig.imageMode,
        // PRINT-5 — capa de perfiles de compatibilidad (ver
        // printerCompatibilityProfiles.js): mismo patrón que los cuatro
        // campos de arriba, solo pasa lo que ya está guardado en la config
        // local de este dispositivo -- no decide nada acá.
        cutStrategyId: printerConfig.cutStrategyId,
        effectivePrintableWidthDots: printerConfig.effectivePrintableWidthDots,
      });
      await printService.printReceipt(receipt, {
        printerName: target.printerName,
        idempotencyKey,
        connector: target.connector,
      });
      // Un duplicado significa que el conector ya lo había enviado: para el
      // cajero es éxito, y no se vuelve a imprimir.
      printAttemptRef.current = { saleKey, key: idempotencyKey, failed: false };
      setPrintStatus('success');
    } catch (err) {
      printAttemptRef.current = { saleKey, key: idempotencyKey, failed: true };
      // PRINT-3A-BUG1: QZ Tray reemplaza cualquier rechazo de la firma por
      // el genérico "Failed to sign request" (ver qzTrayProvider.js, que
      // ya logueó la causa real con logSigningFailure antes de llegar
      // acá). Ese texto es jerga técnica para un cajero -- acá se muestra
      // un mensaje simple y se deja el detalle en consola.
      console.error('[CrmTerminal] fallo al imprimir el ticket:', err);
      setPrintStatus('error');
      setPrintErrorMessage(
        err?.message === 'Failed to sign request'
          ? 'No se pudo autorizar la impresión. La venta ya quedó registrada.'
          : (err?.message || 'No se pudo imprimir el ticket.'),
      );
    }
  }, [ticketData, business]);

  useEffect(() => {
    if (!ticketData) return;
    const ticketId = String(ticketData.sale?.id ?? ticketData.sale?.invoice_number);
    if (!ticketId || printedTicketRef.current === ticketId) return;
    printedTicketRef.current = ticketId;
    printCurrentTicket();
  }, [ticketData, printCurrentTicket]);

  // Debounce search → debouncedSearch
  useEffect(() => {
    clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => setDebouncedSearch(search), 260);
    return () => clearTimeout(searchDebounceRef.current);
  }, [search]);

  // TPV-STOCK-UX-1: extraído del efecto de montaje para poder volver a
  // llamarlo después de un STOCK_INSUFFICIENT server-side (sección 13) --
  // no hay realtime todavía, esto es solo "usar la información más
  // reciente disponible" con un refetch simple.
  const refreshProducts = useCallback(() => {
    if (!business?.id) return;

    // Carga rápida: solo productos visibles en TPV
    getPosProducts(business.id).then((result) => {
      const list = result.data || [];
      const fallback = !!result._fallback;
      console.log('[TPV-diag] getPosProducts →', {
        total: list.length,
        fallback,
        sample: list.slice(0, 3).map(p => ({ id: p.id, name: p.name, show_in_pos: p.show_in_pos })),
      });
      setPosProducts(list);
      setPosFallback(fallback);
      setPosLoading(false);
    });

    // Carga en background: todos los activos para búsqueda global
    getAllActiveProducts(business.id).then(({ data, error }) => {
      const list = data || [];
      console.log('[TPV-diag] getAllActiveProducts →', {
        total: list.length,
        error: error?.message ?? null,
        sample: list.slice(0, 3).map(p => ({ id: p.id, name: p.name })),
      });
      setAllProducts(list);
      allProductsRef.current = list;
    });
  }, [business?.id]);

  useEffect(() => {
    if (!business?.id || !hasAccess) return;

    refreshProducts();

    getCrmCustomers(business.id).then(({ data }) => {
      customers.current = data || [];
      setCustomersDisplay(data || []);
      setCustomersLoaded(true);
    });
  }, [business?.id, hasAccess, refreshProducts]);

  const handleCreateCustomer = async (fields) => {
    const { data, error } = await createCrmCustomer(business.id, fields);
    if (error) return { error };
    customers.current = [data, ...customers.current];
    setCustomersDisplay([data, ...customersDisplay]);
    setCustomerId(data.id);
    setShowNewCustomer(false);
    return { data };
  };

  const categories = useMemo(() => {
    const cats = [...new Set(posProducts.map(p => p.category).filter(Boolean))].sort();
    return cats;
  }, [posProducts]);

  // Sin búsqueda: grilla de productos TPV (show_in_pos), filtrada por categoría.
  // Con búsqueda: resultados de todos los productos activos.
  const isSearching = debouncedSearch.trim().length > 0;

  // Normaliza texto para búsqueda: minúsculas + sin acentos
  const normalize = (s) =>
    (s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

  const filtered = useMemo(() => {
    if (isSearching) {
      const q = normalize(debouncedSearch);
      // Si no hay productos en allProducts todavía, buscamos en posProducts como fallback
      const pool = allProducts.length > 0 ? allProducts : posProducts;
      const results = pool.filter(p =>
        normalize(p.name).includes(q) ||
        (p.public_code && normalize(p.public_code).includes(q)) ||
        (p.sku         && normalize(p.sku).includes(q)) ||
        (p.barcode     && normalize(p.barcode).includes(q)) ||
        (p.category    && normalize(p.category).includes(q))
      ).slice(0, 50);
      console.log(`[TPV-diag] search "${debouncedSearch}" → pool=${pool.length} results=${results.length}`);
      return results;
    }
    let list = posProducts;
    if (activeCategory) list = list.filter(p => p.category === activeCategory);
    return list.slice(0, 40);
  }, [posProducts, allProducts, debouncedSearch, isSearching, activeCategory]);

  // Busca coincidencia exacta de barcode/sku/public_code para lector de código de barras.
  // Siempre busca en TODOS los productos activos para no perder productos no visibles en grilla.
  const findExactProduct = useCallback((code) => {
    const c = code.trim().toLowerCase();
    if (!c) return null;
    const pool = allProductsRef.current.length > 0 ? allProductsRef.current : posProducts;
    return (
      pool.find(p => p.barcode     && p.barcode.toLowerCase()     === c) ||
      pool.find(p => p.sku         && p.sku.toLowerCase()         === c) ||
      pool.find(p => p.public_code && p.public_code.toLowerCase() === c) ||
      null
    );
  }, [posProducts]);

  // ── TPV-STOCK-UX-1: validación de stock en el TPV ─────────────────────────
  // stock_actual === NULL  -> sin control de stock, cantidad ilimitada.
  // stock_actual === 0     -> agotado.
  // stock_actual === N > 0 -> máximo inmediato sugerido por el frontend.
  // La RPC (crm_create_pos_sale, bajo FOR UPDATE) sigue siendo la autoridad
  // final -- esto es solo ayuda de UX para no llegar hasta "Confirmar
  // venta" para enterarse de un límite que el navegador ya conoce.

  // productId -> stock_actual más reciente cargado (posProducts/allProducts,
  // refrescados al montar y tras un STOCK_INSUFFICIENT server-side). Un
  // producto ausente del mapa (todavía no cargado) no bloquea nada acá.
  const stockById = useMemo(() => {
    const map = new Map();
    posProducts.forEach((p) => map.set(p.id, p.stock_actual));
    allProducts.forEach((p) => map.set(p.id, p.stock_actual));
    return map;
  }, [posProducts, allProducts]);

  // Cantidad TOTAL en el carrito por product_id -- suma todas las líneas
  // del mismo producto, nunca valida una línea aislada (sección 6). Este
  // componente ya fusiona líneas del mismo producto en addToCart, pero el
  // cálculo no depende de eso -- sigue sumando todas las que hubiera.
  const cartQtyByProduct = useMemo(() => {
    const map = new Map();
    cart.forEach((item) => {
      if (!item.product_id) return; // línea manual -- nunca tiene stock
      map.set(item.product_id, (map.get(item.product_id) || 0) + item.quantity);
    });
    return map;
  }, [cart]);

  // Líneas cuya cantidad conocida excede el stock disponible más reciente
  // -- por un draft restaurado con una cantidad vieja, o porque otro
  // cajero vendió stock mientras se armaba esta venta. Nunca se ajusta
  // la cantidad sola (sección 8): solo se marca y bloquea Cobrar hasta
  // que el usuario decida (ajustar o eliminar la línea).
  const cartStockIssues = useMemo(() => {
    const issues = new Map(); // product_id -> { available, requested }
    cart.forEach((item) => {
      if (!item.product_id) return;
      const available = stockById.get(item.product_id);
      if (available === null || available === undefined) return; // sin control de stock, o aún no cargado
      const requested = cartQtyByProduct.get(item.product_id) || item.quantity;
      if (requested > available) issues.set(item.product_id, { available, requested });
    });
    return issues;
  }, [cart, stockById, cartQtyByProduct]);

  const hasStockIssues = cartStockIssues.size > 0;

  const addToCart = (product) => {
    const stockLimit = product.stock_actual; // null = sin control de stock
    setCart(prev => {
      if (prev.length === 0) getOrCreateSaleIdempotencyKey();
      const existing = prev.find(i => i._key === product.id);
      const currentQty = existing ? existing.quantity : 0;
      if (stockLimit !== null && stockLimit !== undefined && currentQty + 1 > stockLimit) {
        setErrorMsg(`No hay más stock disponible de ${product.name}. Disponible: ${stockLimit}.`);
        return prev;
      }
      if (existing) {
        return prev.map(i => i._key === product.id ? { ...i, quantity: i.quantity + 1 } : i);
      }
      return [...prev, {
        _key: product.id,
        product_id: product.id,
        name: product.name,
        unit_price: product.price || 0,
        quantity: 1,
      }];
    });
  };

  const addManualItem = ({ name, unit_price, quantity, note }) => {
    const _key = `manual_${Date.now()}`;
    setCart(prev => {
      if (prev.length === 0) getOrCreateSaleIdempotencyKey();
      return [...prev, { _key, product_id: null, name, unit_price, quantity, note: note || null }];
    });
  };

  const updateQty = (_key, delta) => {
    setCart(prev =>
      prev
        .map(i => {
          if (i._key !== _key) return i;
          if (delta > 0 && i.product_id) {
            const stockLimit = stockById.get(i.product_id);
            if (stockLimit !== null && stockLimit !== undefined) {
              const totalForProduct = cartQtyByProduct.get(i.product_id) || i.quantity;
              if (totalForProduct + delta > stockLimit) return i; // no incrementa más allá del stock disponible
            }
          }
          return { ...i, quantity: i.quantity + delta };
        })
        .filter(i => i.quantity > 0)
    );
  };

  const removeItem = (_key) => {
    setCart(prev => prev.filter(i => i._key !== _key));
  };

  // Reconciliación de draft/concurrencia (sección 8): reduce la cantidad
  // de una línea a un valor exacto (ej. "Ajustar a 2") -- solo por acción
  // explícita del usuario, nunca automático.
  const setCartQuantityTo = (_key, quantity) => {
    setCart(prev => prev.map(i => i._key === _key ? { ...i, quantity } : i).filter(i => i.quantity > 0));
  };

  const subtotal = cart.reduce((s, i) => s + i.unit_price * i.quantity, 0);
  const discountAmount = Math.min(parseMoneyInput(discount), subtotal);
  const total = subtotal - discountAmount;
  const cartCount = cart.reduce((s, i) => s + i.quantity, 0);

  const selectedCustomer = customersDisplay.find(c => c.id === customerId) || null;
  const parsedPayments = payments
    .map((payment) => ({
      ...payment,
      amountNumber: parseMoneyInput(payment.amount),
    }))
    .filter((payment) => payment.amountNumber > 0);
  const nonCashTotal = parsedPayments
    .filter((payment) => payment.method !== 'cash')
    .reduce((sum, payment) => sum + payment.amountNumber, 0);
  const cashTendered = parsedPayments
    .filter((payment) => payment.method === 'cash')
    .reduce((sum, payment) => sum + payment.amountNumber, 0);
  const cashApplied = Math.min(cashTendered, Math.max(0, total - nonCashTotal));
  const paidTotal = Math.min(total, nonCashTotal + cashApplied);
  const pendingBalance = Math.max(0, total - paidTotal);
  const change = Math.max(0, cashTendered - cashApplied);
  const hasNonCashOverpay = nonCashTotal > total;
  const requiresCustomerForPending = pendingBalance > 0;
  const isPaymentInvalid = hasNonCashOverpay || (requiresCustomerForPending && !customerId);
  const paymentStatusLabel = pendingBalance <= 0 ? 'Pagada' : paidTotal > 0 ? 'Parcial' : 'Pendiente';
  const appliedPayments = parsedPayments
    .reduce((acc, payment) => {
      if (payment.method !== 'cash') {
        acc.push({ method: payment.method, amount: payment.amountNumber });
        return acc;
      }
      const alreadyApplied = acc
        .filter((entry) => entry.method === 'cash')
        .reduce((sum, entry) => sum + entry.amount, 0);
      const remainingCash = Math.max(0, cashApplied - alreadyApplied);
      const amount = Math.min(payment.amountNumber, remainingCash);
      if (amount > 0) acc.push({ method: 'cash', amount });
      return acc;
    }, [])
    .map((payment) => ({ ...payment, amount: +payment.amount.toFixed(2) }));

  const resetForm = () => {
    // Reset real de intento de venta: la próxima "Completar venta" debe
    // generar una idempotency key nueva, nunca reutilizar la del intento
    // anterior (ya completado o descartado).
    saleIdempotencyKeyRef.current = null;
    draftDebouncerRef.current.cancel();
    if (draftKey) removePosTerminalDraft(draftKey);
    setDraftNotice(null);
    setCustomerRemovedNotice(false);
    setCheckoutStep('sale');
    setCart([]);
    setCustomerId('');
    setDiscount('');
    setPayments([{ id: `payment_${Date.now()}`, method: 'cash', amount: '' }]);
    setSplitPaymentMode(false);
    setNotes('');
    setSearch('');
    setActiveCategory('');
    setErrorMsg(null);
    setTimeout(() => searchRef.current?.focus(), 50);
  };

  const refreshHeldSales = useCallback(async () => {
    if (!business?.id) return;
    const { data, error } = await listHeldPosSales(business.id);
    if (error) {
      setErrorMsg(error.message);
      return;
    }
    setHeldSales(data || []);
  }, [business?.id]);

  useEffect(() => {
    if (!hasAccess) return;
    refreshHeldSales();
  }, [hasAccess, refreshHeldSales]);

  const handleHoldSale = async () => {
    if (!business?.id || cart.length === 0 || heldBusy || checkoutStep !== 'sale') return;
    setHeldBusy(true);
    setErrorMsg(null);
    const { error } = await holdPosSale(business.id, {
      customerId: customerId || null,
      items: cart,
      discount: discountAmount,
      notes: notes || null,
      label: selectedCustomer?.name || null,
    });
    if (error) {
      setErrorMsg(error.message);
      setHeldBusy(false);
      return;
    }
    // El servidor confirmó el hold: recién ahora se libera esta caja.
    resetForm();
    await refreshHeldSales();
    setHeldBusy(false);
  };

  const handleResumeHeldSale = async (heldSaleId) => {
    if (!business?.id || heldBusy) return;
    if (cart.length > 0) {
      setErrorMsg('Deja en espera o termina la venta actual antes de recuperar otra.');
      return;
    }
    setHeldBusy(true);
    setErrorMsg(null);
    const { data, error } = await claimHeldPosSale(business.id, heldSaleId);
    if (error) {
      setErrorMsg(error.message);
      await refreshHeldSales();
      setHeldBusy(false);
      return;
    }

    const claimToken = data?.claim_token;
    try {
      const restoredItems = (data?.items || []).map((item, index) => ({
        _key: item.product_id || `manual_held_${data.id}_${index}`,
        product_id: item.product_id || null,
        name: item.name,
        unit_price: Number(item.unit_price),
        quantity: Number(item.quantity),
        note: item.note || null,
      }));
      if (restoredItems.length === 0) throw new Error('La venta en espera no contiene artículos.');

      const restoredPayments = [{ id: `payment_${Date.now()}`, method: 'cash', amount: '' }];
      const restoredDiscount = Number(data.discount || 0) > 0 ? String(Number(data.discount)) : '';
      // Es una venta retomada distinta de cualquier intento vacío/anterior.
      saleIdempotencyKeyRef.current = null;
      const newIdempotencyKey = getOrCreateSaleIdempotencyKey();

      setCart(restoredItems);
      setCustomerId(data.customer_id || '');
      setDiscount(restoredDiscount);
      setNotes(data.notes || '');
      setPayments(restoredPayments);
      setCheckoutStep('sale');

      // Guardar protección local antes de eliminar el hold servidor.
      if (draftKey) {
        writePosTerminalDraft(draftKey, buildPosTerminalDraftSnapshot({
          cart: restoredItems,
          customerId: data.customer_id || '',
          discount: restoredDiscount,
          notes: data.notes || '',
          payments: restoredPayments,
          idempotencyKey: newIdempotencyKey,
        }));
      }

      const consumed = await consumeClaimedPosSale(business.id, data.id, claimToken);
      if (consumed.error) {
        setErrorMsg('La venta fue recuperada, pero su registro en espera sigue protegido. No la recuperes desde otra caja.');
      } else {
        setHeldSales((prev) => prev.filter((sale) => sale.id !== data.id));
      }
      setHeldSalesOpen(false);
    } catch (resumeError) {
      if (claimToken) await releaseClaimedPosSale(business.id, heldSaleId, claimToken);
      setErrorMsg(resumeError?.message || 'No se pudo recuperar la venta en espera.');
      await refreshHeldSales();
    } finally {
      setHeldBusy(false);
    }
  };

  const handleDiscardHeldSale = async (heldSaleId) => {
    if (!business?.id || heldBusy) return;
    if (!window.confirm('¿Descartar esta venta en espera? Esta acción no se puede deshacer.')) return;
    setHeldBusy(true);
    setErrorMsg(null);
    const { error } = await discardHeldPosSale(business.id, heldSaleId);
    if (error) setErrorMsg(error.message);
    await refreshHeldSales();
    setHeldBusy(false);
  };

  const updatePayment = (id, updates) => {
    setPayments((prev) => prev.map((payment) => (
      payment.id === id ? { ...payment, ...updates } : payment
    )));
  };

  const addPayment = () => {
    setPayments((prev) => [
      ...prev,
      { id: `payment_${Date.now()}_${prev.length}`, method: 'debit_card', amount: '' },
    ]);
  };

  const removePayment = (id) => {
    setPayments((prev) => {
      const next = prev.filter((payment) => payment.id !== id);
      return next.length > 0 ? next : [{ id: `payment_${Date.now()}`, method: 'cash', amount: '' }];
    });
  };

  // TPV-CHECKOUT-UX-1: un toque selecciona el medio y completa el saldo.
  // No cambia la lógica contable: solo prepara el mismo array payments que
  // handleRegister/appliedPayments ya consumen.
  const selectQuickPaymentMethod = (method) => {
    const amount = String(Math.max(0, total));
    setSplitPaymentMode(false);
    setPayments([{ id: `payment_${Date.now()}`, method, amount }]);
  };

  // Pago dividido rápido: al activarlo partimos con el primer medio vacío
  // para que el cajero escriba cuánto recibió; el segundo medio puede tomar
  // el saldo restante con un toque.
  const startSplitPayment = () => {
    const firstMethod = payments[0]?.method || 'cash';
    setSplitPaymentMode(true);
    setPayments([{ id: `payment_${Date.now()}`, method: firstMethod, amount: '' }]);
  };

  const addPaymentForRemainingBalance = () => {
    const remaining = Math.max(0, pendingBalance);
    setSplitPaymentMode(true);
    setPayments((prev) => [
      ...prev,
      {
        id: `payment_${Date.now()}_${prev.length}`,
        method: prev.some((payment) => payment.method === 'debit_card') ? 'cash' : 'debit_card',
        amount: remaining > 0 ? String(remaining) : '',
      },
    ]);
  };

  // ── TPV-CORE-3: flujo de checkout en dos etapas ───────────────────────────
  // Estado puramente de UI -- no crea ni modifica ningún registro. Nunca
  // borra carrito/cliente/descuento/notas/pagos/idempotency key/draft.
  const handleGoToPayment = () => {
    if (cart.length === 0) return;
    // TPV-STOCK-UX-1: si el frontend ya sabe que hay una línea sin stock
    // suficiente, ni siquiera deja pasar a la etapa de cobro -- el botón
    // ya está disabled en la UI, este es el mismo guard defense-in-depth
    // que el resto del componente usa junto a cada disabled=.
    if (hasStockIssues) return;
    // La venta normal entra lista para efectivo exacto: el cajero puede
    // confirmar de inmediato o reemplazar el monto si recibió más dinero.
    // Si ya existe una configuración de pago restaurada/editada, se conserva.
    setPayments((prev) => {
      const isFreshCash = prev.length === 1 && prev[0]?.method === 'cash' && !parseMoneyInput(prev[0]?.amount);
      return isFreshCash
        ? [{ ...prev[0], amount: String(Math.max(0, total)) }]
        : prev;
    });
    setSplitPaymentMode(false);
    setCheckoutStep('payment');
  };

  const handleBackToSale = () => {
    setSplitPaymentMode(false);
    setCheckoutStep('sale');
  };

  // ── TPV-CORE-2: borrador local de la venta en curso ──────────────────────
  const checkedDraftKeyRef = useRef(null);

  const applyDraft = useCallback((draft) => {
    setCart(draft.cart);
    setCustomerId(draft.customerId || '');
    setDiscount(draft.discount || '');
    setNotes(draft.notes || '');
    if (draft.payments.length > 0) setPayments(draft.payments);
    // Restaurar EXACTAMENTE la key del borrador -- nunca generar una nueva
    // acá, o un reintento después de restaurar duplicaría la venta.
    saleIdempotencyKeyRef.current = draft.idempotencyKey;
  }, []);

  // Restauración al montar -- una sola vez por businessId/draftKey. Si el
  // borrador es del mismo día local, se restaura solo con aviso discreto;
  // si es de otro día, se pide una decisión explícita (nunca en silencio).
  useEffect(() => {
    if (!draftKey) return;
    if (checkedDraftKeyRef.current === draftKey) return;
    checkedDraftKeyRef.current = draftKey;

    const draft = readPosTerminalDraft(draftKey);
    if (!draft) {
      restoreCheckedRef.current = true;
      return;
    }
    if (isDraftFromToday(draft.savedAt)) {
      applyDraft(draft);
      setDraftNotice('restored');
      restoreCheckedRef.current = true;
    } else {
      setDraftNotice({ pending: draft });
      // restoreCheckedRef solo se marca cuando el usuario decide -- así el
      // autosave no puede pisar un borrador todavía no confirmado.
    }
  }, [draftKey, applyDraft]);

  const handleRecoverStaleDraft = () => {
    if (!draftNotice?.pending) return;
    applyDraft(draftNotice.pending);
    setDraftNotice('restored');
    restoreCheckedRef.current = true;
  };

  const handleDiscardStaleDraft = () => {
    if (draftKey) removePosTerminalDraft(draftKey);
    setDraftNotice(null);
    restoreCheckedRef.current = true;
  };

  // Cliente eliminado: si el customerId restaurado ya no existe entre los
  // clientes del negocio, se limpia sin crashear y sin convertir en
  // silencio a consumidor final -- se informa explícitamente.
  useEffect(() => {
    if (!customersLoaded || !customerId) return;
    const exists = customersDisplay.some((c) => c.id === customerId);
    if (!exists) {
      setCustomerId('');
      setCustomerRemovedNotice(true);
    }
  }, [customersLoaded, customersDisplay, customerId]);

  const persistDraftNow = useCallback(() => {
    if (!draftKey || cart.length === 0) return;
    const snapshot = buildPosTerminalDraftSnapshot({
      cart,
      customerId,
      discount,
      notes,
      payments,
      idempotencyKey: saleIdempotencyKeyRef.current,
    });
    writePosTerminalDraft(draftKey, snapshot);
  }, [draftKey, cart, customerId, discount, notes, payments]);

  const persistDraftNowRef = useRef(persistDraftNow);
  persistDraftNowRef.current = persistDraftNow;

  const flushDraft = useCallback(() => {
    draftDebouncerRef.current.flush(() => persistDraftNowRef.current());
  }, []);

  // Autosave debounced. Doble gate: restoreCheckedRef (nunca pisar un
  // borrador todavía no decidido por el usuario) y !ticketData (no
  // reprogramar mientras se muestra el ticket de una venta ya completada).
  useEffect(() => {
    if (!restoreCheckedRef.current || ticketData) return;
    draftDebouncerRef.current.schedule(() => persistDraftNowRef.current());
  }, [cart, customerId, discount, notes, payments, ticketData]);

  useEffect(() => {
    if (!draftKey) return;
    const handleVisibility = () => { if (document.hidden) flushDraft(); };
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('beforeunload', flushDraft);
    window.addEventListener('pagehide', flushDraft);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('beforeunload', flushDraft);
      window.removeEventListener('pagehide', flushDraft);
    };
  }, [draftKey, flushDraft]);

  useEffect(() => () => {
    // React Router navigation solo desmonta el componente -- no dispara de
    // forma confiable blur/visibilitychange/beforeunload. Nunca reemplazar
    // este flush por un cancel() o se pierde el último cambio sin guardar.
    draftDebouncerRef.current.flush(() => persistDraftNowRef.current());
  }, []);

  const loadPointTerminals = useCallback(async () => {
    setPointLoading(true); setPointError(null); setPointErrorReason(null);
    const [terminalsResult, preferenceResult, connectionResult] = await Promise.all([
      getPointTerminals(),
      getPointTerminalPreference(),
      getPointConnection(),
    ]);
    setPointLoading(false);
    if (terminalsResult.error) {
      setPointError(terminalsResult.error.message);
      setPointErrorReason(terminalsResult.error.reason || null);
      return;
    }
    if (preferenceResult.error) {
      setPointError(preferenceResult.error.message);
      setPointErrorReason(preferenceResult.error.reason || null);
      return;
    }
    if (connectionResult.error) {
      setPointError(connectionResult.error.message);
      setPointErrorReason(connectionResult.error.reason || null);
      return;
    }
    setPointConnection(connectionResult.data?.connection || null);
    const terminals = Array.isArray(terminalsResult.data?.terminals) ? terminalsResult.data.terminals : [];
    const preference = preferenceResult.data?.preference || null;
    setPointTerminals(terminals);
    setPointPreference(preference);
    // Nunca elegir una terminal por ser la primera o por informar PDV.
    // Solo la preferencia persistida y explícita del comercio es la Point activa.
    setPointTerminalId(
      preference?.terminal_id && terminals.some(t => t.id === preference.terminal_id)
        ? preference.terminal_id
        : ''
    );
  }, []);

  const handleSelectPointTerminal = async (terminalId) => {
    if (pointOperation?.operation_id) return;
    setPointLoading(true); setPointError(null); setPointErrorReason(null);
    const { data, error } = await selectPointTerminal(terminalId);
    setPointLoading(false);
    if (error) {
      setPointError(error.message);
      setPointErrorReason(error.reason || null);
      return;
    }
    setPointPreference(data?.preference || { terminal_id: terminalId, verification_status: 'pending' });
    setPointTerminalId(terminalId);
  };

  const handleVerifyPointTerminal = async () => {
    if (!pointTerminalId || pointOperation?.operation_id) return;
    setPointLoading(true); setPointError(null); setPointErrorReason(null);
    const { data, error } = await verifyPointTerminal(pointTerminalId);
    setPointLoading(false);
    if (error) {
      setPointError(error.message);
      setPointErrorReason(error.reason || null);
      return;
    }
    setPointPreference(data?.preference || null);
  };

  const handleUnlinkPointTerminal = async () => {
    if (pointOperation?.operation_id) return;
    setPointLoading(true); setPointError(null); setPointErrorReason(null);
    const { error } = await unlinkPointTerminal();
    setPointLoading(false);
    if (error) {
      setPointError(error.message);
      setPointErrorReason(error.reason || null);
      return;
    }
    setPointPreference(null);
    setPointTerminalId('');
  };

  const handleConnectPoint = useCallback(async () => {
    setPointLoading(true); setPointError(null); setPointErrorReason(null);
    const { data, error } = await startPointOauth();
    setPointLoading(false);
    if (error || !data?.authorizationUrl) {
      setPointError(error?.message || 'No pudimos iniciar la conexión con Mercado Pago Point.');
      return;
    }
    window.location.assign(data.authorizationUrl);
  }, []);

  const handleDisconnectPointConnection = useCallback(async () => {
    if (pointOperation?.operation_id) return;
    setPointLoading(true); setPointError(null); setPointErrorReason(null);
    const { error } = await disconnectPointConnection();
    setPointLoading(false);
    if (error) {
      setPointError(error.message);
      setPointErrorReason(error.reason || null);
      return;
    }
    setPointConnection(null);
    setPointPreference(null);
    setPointTerminalId('');
    setPointTerminals([]);
  }, [pointOperation]);

  useEffect(() => {
    if (checkoutStep === 'payment' && pointTerminals.length === 0 && !pointLoading && !pointError) loadPointTerminals();
  }, [checkoutStep, pointTerminals.length, pointLoading, pointError, loadPointTerminals]);

  // Recuperación después de refresh/cierre de pestaña: solo guardamos el
  // operationId. El estado authoritative se vuelve a pedir al backend.
  useEffect(() => {
    if (!pointStorageKey || pointOperation) return;
    try {
      const operationId = localStorage.getItem(pointStorageKey);
      if (operationId) setPointOperation({ operation_id: operationId, status: 'recovering' });
    } catch { /* localStorage no es autoridad */ }
  }, [pointStorageKey, pointOperation]);

  const finishPointUi = useCallback((data) => {
    if (!data?.invoice_id) return;
    const saleSnapshot = {
      items: [...cart], customer: selectedCustomer, paymentMethod: 'mercado_pago',
      payments: [{ method: 'mercado_pago', amount: total }],
      discountAmount, subtotal, total, amountReceived: null, change: null,
      initialPaymentAmount: null, initialPaymentMethod: null, pendingBalance: 0,
      paymentStatus: 'Pagada', notes: notes || null, createdAt: new Date().toISOString(),
    };
    draftDebouncerRef.current.cancel();
    if (draftKey) removePosTerminalDraft(draftKey);
    if (pointStorageKey) localStorage.removeItem(pointStorageKey);
    setPointOperation(null);
    setTicketData({ sale: data.sale || { id: data.invoice_id }, ...saleSnapshot });
    refreshProducts();
  }, [cart, selectedCustomer, total, discountAmount, subtotal, notes, draftKey, pointStorageKey]);

  const pollPointOnce = useCallback(async () => {
    const operationId = pointOperation?.operation_id;
    if (!operationId) return;
    const { data, error } = await getPointOrder(operationId);
    if (error && !data) { setPointError(error.message); return; }
    const next = data || {};
    setPointOperation(prev => ({ ...prev, ...next }));
    // Supabase Functions surfaces non-2xx responses as { data, error }. A 202
    // from mp-point-get-order is intentionally recoverable: the payment may
    // already be processed while the local sale finalizer is still pending.
    // Keep the same operation/idempotency keys and continue polling, but make
    // the pending recovery state visible to the cashier.
    // Una invoice confirmada gana incluso si el transporte envolvió la
    // respuesta con error: la venta ya existe y es seguro cerrar la UI.
    if (next.invoice_id) { finishPointUi(next); return; }

    // Cualquier non-2xx es recuperable desde la perspectiva del cajero:
    // conservar operationId + claves idempotentes y seguir consultando. En
    // particular LOCAL_RELEASE_FAILED puede traer status='canceled'; NO se
    // debe habilitar un cobro nuevo hasta que el backend confirme cleanup.
    if (error) {
      if (error.reason === 'POINT_FINALIZATION_PENDING') {
        setPointError('Pago aprobado. Walinka está terminando de registrar la venta; no vuelvas a cobrar.');
      } else if (error.reason === 'LOCAL_RELEASE_FAILED') {
        setPointError('El cobro terminó, pero Walinka aún está liberando la reserva de stock. Espera antes de intentar nuevamente.');
      } else {
        setPointError(error.message || 'No pudimos confirmar el estado del cobro. Walinka seguirá consultándolo.');
      }
      return;
    }
    setPointError(null);

    if (['failed','expired','canceled','refunded'].includes(next.status)) {
      if (pointStorageKey) localStorage.removeItem(pointStorageKey);
      // Un estado terminal significa que este intento de cobro ya no puede
      // convertirse en venta. Liberamos la operación de la UI y renovamos
      // AMBAS claves para que un nuevo intento sea una operación/venta nueva.
      // Mientras el estado sea ambiguo (created/at_terminal/action_required)
      // jamás hacemos esto: allí se conserva la idempotencia original.
      setPointLastResult({
        operation_id: operationId,
        order_id: next.order_id || null,
        status: next.status,
        status_detail: next.status_detail || null,
        payment_status: next.payment_status || null,
        payment_status_detail: next.payment_status_detail || null,
      });
      setPointOperation(null);
      pointCreateKeyRef.current = null;
      saleIdempotencyKeyRef.current = null;
      setPointError(next.status === 'expired' ? 'El cobro Point venció. Puedes intentar nuevamente.' : `El cobro Point terminó como ${next.status}. Puedes intentar nuevamente.`);
      refreshProducts();
    }
  }, [pointOperation?.operation_id, pointStorageKey, finishPointUi]);

  useEffect(() => {
    if (!pointOperation?.operation_id || pointOperation?.invoice_id) return;

    // Point Orders puede aplicar rate limiting si consultamos demasiado rápido.
    // Usamos un timeout encadenado (no setInterval) para garantizar que nunca
    // haya dos GET solapados y dejamos 5 s entre consultas. La primera consulta
    // sigue siendo inmediata para que el cajero vea el cambio de estado rápido.
    let cancelled = false;
    const poll = async () => {
      await pollPointOnce();
      if (!cancelled) pointPollRef.current = window.setTimeout(poll, 5000);
    };
    poll();

    return () => {
      cancelled = true;
      if (pointPollRef.current) window.clearTimeout(pointPollRef.current);
      pointPollRef.current = null;
    };
  }, [pointOperation?.operation_id, pointOperation?.invoice_id, pollPointOnce]);

  const handleSetupPoint = async () => {
    if (!pointTerminalId) return;
    setPointLoading(true); setPointError(null);
    const { data, error } = await setupPointTerminal(pointTerminalId);
    setPointLoading(false);
    if (error) { setPointError(error.message); return; }
    await loadPointTerminals();
    if (data?.restart_required) setPointError('Modo PDV activado. Reinicia físicamente la Point antes de cobrar.');
  };

  const handlePointCharge = async () => {
    if (submitLockRef.current || pointOperation?.operation_id) return;
    if (!pointTerminalId || cart.length === 0 || total <= 0) return;
    if (pointPreference?.terminal_id !== pointTerminalId) {
      setPointError('Selecciona explícitamente la Point que usará este negocio.');
      return;
    }
    if (pointPreference?.verification_status !== 'verified') {
      setPointError('Esta Point todavía está pendiente de verificación física.');
      return;
    }
    const terminal = pointTerminals.find(t => t.id === pointTerminalId);
    if (terminal?.operating_mode !== 'PDV') { setPointError('Activa el modo PDV antes de cobrar.'); return; }

    submitLockRef.current = true; setPointLoading(true); setPointError(null);
    try {
      const { data: openSession } = await getOpenCashSession(business.id);
      if (!openSession) { setPointError('Debes abrir caja antes de cobrar con Point.'); return; }
      // El nacimiento de una operación Point define un par inseparable de
      // claves. Si no existe createKey NO estamos reintentando una llamada
      // ambigua: es un cobro Point nuevo. En ese momento reemplazamos también
      // la sale key aunque el borrador del TPV haya restaurado una antigua.
      // Una vez creada pointCreateKeyRef, los reintentos de red conservan
      // ambas claves exactamente iguales.
      if (!pointCreateKeyRef.current) {
        pointCreateKeyRef.current = crypto.randomUUID();
        saleIdempotencyKeyRef.current = crypto.randomUUID();
      }
      const { data, error } = await createPointOrder({
        terminalId: pointTerminalId, items: cart, customerId: customerId || null,
        discount: discountAmount, notes: notes || null,
        createIdempotencyKey: pointCreateKeyRef.current,
        saleIdempotencyKey: saleIdempotencyKeyRef.current,
      });
      if (data?.operation_id) {
        setPointOperation(data);
        if (pointStorageKey) localStorage.setItem(pointStorageKey, data.operation_id);
      }
      if (error) setPointError(error.message);
    } finally {
      submitLockRef.current = false; setPointLoading(false);
    }
  };

  const handleCancelPoint = async () => {
    const operationId = pointOperation?.operation_id;
    if (!operationId || pointCanceling || pointOperation?.status === 'processed') return;
    // Cancelar es una acción independiente del loading usado por polling,
    // terminal discovery y setup. Si el polling mantiene pointLoading o una
    // consulta se solapa, el cajero igual debe poder solicitar la cancelación.
    setPointCanceling(true); setPointError(null);
    try {
      const { data, error } = await cancelPointOrder(operationId);
      if (error) {
        setPointError(error.message || 'No pudimos confirmar la cancelación. Walinka seguirá consultando el mismo cobro.');
        return;
      }
      if (data?.status === 'canceled') {
        if (pointStorageKey) localStorage.removeItem(pointStorageKey);
        setPointLastResult({
          operation_id: operationId,
          order_id: data?.order_id || null,
          status: 'canceled',
          status_detail: data?.status_detail || null,
          payment_status: data?.payment_status || null,
          payment_status_detail: data?.payment_status_detail || null,
        });
        setPointOperation(null);
        // La operación cancelada nunca puede finalizar una venta. Un nuevo
        // cobro debe tener nuevas create/sale idempotency keys; reutilizar la
        // sale key chocaría correctamente con el UNIQUE de operaciones Point.
        pointCreateKeyRef.current = null;
        saleIdempotencyKeyRef.current = null;
        refreshProducts();
      } else {
        // Una respuesta 202/estado aún no terminal NO habilita otro cobro.
        // Conservamos operationId e idempotencia y dejamos que polling
        // reconcilie el resultado authoritative.
        setPointError('Mercado Pago recibió la cancelación, pero todavía no confirmó el estado final.');
      }
    } finally {
      setPointCanceling(false);
    }
  };

  const handleRegister = async () => {
    // Lock síncrono -- ver comentario junto a la declaración de
    // submitLockRef. disabled={busy} en el botón ya cubre el caso normal;
    // esto cubre la ventana de un doble click antes del re-render.
    if (submitLockRef.current) return;
    if (cart.length === 0) return;
    submitLockRef.current = true;
    setBusy(true);
    setErrorMsg(null);

    try {
      if (hasNonCashOverpay) {
        setErrorMsg('Solo el efectivo puede generar vuelto.');
        return;
      }

      // El saldo pendiente pasa a cuenta corriente y requiere cliente registrado.
      if (requiresCustomerForPending && !customerId) {
        setErrorMsg('CREDIT_NO_CUSTOMER');
        return;
      }

      // Un pago Mercado Pago manual no ejecuta una orden Point. Confirmación explícita
      // para impedir registrar intentos fallidos como dinero efectivamente recibido.
      if (appliedPayments.some((payment) => payment.method === 'mercado_pago')) {
        const confirmed = window.confirm(
          'Pago Mercado Pago EXTERNO: esta opción NO envía cobros a la máquina Point.\\n\\n' +
          '¿Ya verificaste en Mercado Pago que recibiste el dinero?\\n\\n' +
          'Si quieres cobrar con la máquina, pulsa Cancelar y usa "Mercado Pago Point".'
        );
        if (!confirmed) return;
      }

      // Guard: real payments require open cash session. Pure current account does not touch caja.
      // Chequeo rápido client-side para feedback inmediato -- crm_create_pos_sale
      // vuelve a validarlo server-side de todos modos (autoridad real).
      if (appliedPayments.length > 0) {
        const { data: openSession } = await getOpenCashSession(business.id);
        if (!openSession) {
          setErrorMsg('NO_OPEN_CASH');
          return;
        }
      }

      const saleSnapshot = {
        items: [...cart],
        customer: selectedCustomer,
        paymentMethod: pendingBalance > 0 ? 'credit' : (appliedPayments[0]?.method || 'credit'),
        payments: appliedPayments,
        discountAmount,
        subtotal,
        total,
        amountReceived: cashTendered > 0 ? cashTendered : null,
        change: change > 0 ? change : null,
        initialPaymentAmount: pendingBalance > 0 && paidTotal > 0 ? paidTotal : null,
        initialPaymentMethod: pendingBalance > 0 ? (appliedPayments[0]?.method || null) : null,
        pendingBalance,
        paymentStatus: paymentStatusLabel,
        notes: notes || null,
        createdAt: new Date().toISOString(),
      };

      const { data, error } = await createPosInvoice(business.id, {
        customerId: customerId || null,
        items: cart,
        discount: discountAmount,
        notes: notes || null,
        currency: business?.currency || 'CLP',
        payments: appliedPayments,
        idempotencyKey: getOrCreateSaleIdempotencyKey(),
      });

      if (error) {
        // TPV-STOCK-UX-1: concurrencia real -- otro cajero vendió stock
        // mientras se armaba esta venta y la validación frontend (con
        // datos ya desactualizados) no lo detectó. La RPC es quien lo
        // atrapa bajo lock (autoridad final, nunca se debilita). El
        // nombre del producto se resuelve del propio carrito que se
        // acaba de enviar -- la RPC nunca lo devuelve (ver crmService.js).
        // Nunca borra cart/payments/customerId/discount/notes/key/draft:
        // solo vuelve a 'sale' para que el cajero pueda corregir y
        // reintentar con la MISMA idempotency key.
        if (error.code === 'STOCK_INSUFFICIENT' && error.stockInsufficient) {
          const { productId, requested, available } = error.stockInsufficient;
          const productName = cart.find((i) => i.product_id === productId)?.name || 'un producto';
          setErrorMsg(`Stock insuficiente para ${productName}. Solicitado: ${requested} · Disponible: ${available}.`);
          setCheckoutStep('sale');
          refreshProducts();
        } else {
          setErrorMsg(error.message || 'No se pudo registrar el pago de la venta.');
        }
        return;
      }

      // Venta completada -- el borrador ya no representa nada en curso.
      // Ver nota en el efecto de autosave: mientras ticketData esté seteado
      // no se vuelve a programar una escritura de borrador.
      draftDebouncerRef.current.cancel();
      if (draftKey) removePosTerminalDraft(draftKey);
      setTicketData({ sale: data, ...saleSnapshot });
    } finally {
      submitLockRef.current = false;
      setBusy(false);
    }
  };

  const handleCloseTicket = () => {
    setTicketData(null);
    setPrintStatus('idle');
    setPrintErrorMessage(null);
  };

  // Reutilizada tanto por "Reimprimir" como por "Reintentar impresión":
  // ambas reenvían el mismo ticket ya construido, nunca vuelven a crear
  // la venta ni a tocar pagos/stock.
  const handleReprint = useCallback(() => {
    reprintRequestedRef.current = true;
    printCurrentTicket();
  }, [printCurrentTicket]);

  const handleGoToPrintSettings = useCallback(() => {
    navigate('/crm/impresion');
  }, [navigate]);

  const handleNewSale = () => {
    setTicketData(null);
    setPrintStatus('idle');
    setPrintErrorMessage(null);
    resetForm();
  };

  // ── Sidebar layout helpers ──────────────────────────────────────────────────
  const sidebarWidth = sidebarCollapsed ? 'var(--sidebar-collapsed-width)' : 'var(--sidebar-width)';
  const mainMarginLeft = isDesktop ? sidebarWidth : 0;

  const collapseMenuBtn = isDesktop ? (
    <button
      onClick={() => setSidebarCollapsed(prev => !prev)}
      className="flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-medium transition-all hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      style={{
        fontFamily: 'var(--font-caption)',
        color: 'var(--color-muted-foreground)',
        borderColor: 'var(--color-border)',
      }}
      title={sidebarCollapsed ? 'Expandir menú lateral' : 'Colapsar menú lateral'}
      aria-label={sidebarCollapsed ? 'Expandir menú lateral' : 'Colapsar menú lateral'}
    >
      <Icon name={sidebarCollapsed ? 'PanelLeftOpen' : 'PanelLeftClose'} size={14} color="currentColor" />
      {sidebarCollapsed ? 'Expandir menú' : 'Colapsar menú'}
    </button>
  ) : null;

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--color-background)' }}>

      {/* Sidebar — siempre visible en desktop, solo colapsable */}
      <BusinessSidebar
        isCollapsed={sidebarCollapsed}
        onCollapsedChange={setSidebarCollapsed}
      />

      {/* Main */}
      <main
        className="flex flex-col min-h-screen"
        style={{ marginLeft: mainMarginLeft, transition: 'margin-left var(--transition-base)' }}
      >

        {/* ── PLAN GATE ──────────────────────────────────────────────────────── */}
        {!hasAccess && (
          <>
            <PanelHeader
              title={<><CrmBreadcrumb section="Terminal TPV" /><h1 className="text-base font-bold" style={{ fontFamily: 'var(--font-heading)', color: 'var(--color-foreground)', letterSpacing: '-0.02em' }}>Terminal de ventas</h1></>}
            >
              {collapseMenuBtn}
            </PanelHeader>
            <div className="flex flex-col items-center justify-center py-24 gap-4 px-6">
              <div className="w-16 h-16 rounded-2xl bg-amber-50 flex items-center justify-center">
                <Icon name="Lock" size={28} className="text-amber-500" />
              </div>
              <p className="text-gray-700 font-semibold text-lg">Disponible en plan Full</p>
              <p className="text-gray-400 text-sm">El Terminal de ventas requiere plan Business/Full.</p>
              <button
                onClick={() => navigate('/planes')}
                className="mt-2 px-6 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-600 text-white font-semibold text-sm"
              >
                Ver planes
              </button>
            </div>
          </>
        )}

        {/* ── MAIN POS ───────────────────────────────────────────────────────── */}
        {hasAccess && (
          <>
            <PanelHeader
              title={
                <div className="min-w-0">
                  <CrmBreadcrumb section="Terminal TPV" />
                  <div className="mt-1 flex flex-wrap items-center gap-2">
                    <h1 className="text-xl font-black tracking-tight text-gray-950 sm:text-2xl" style={{ fontFamily: 'var(--font-heading)' }}>Terminal de ventas</h1>
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-0.5 text-xs font-bold text-emerald-700">
                      <span className="h-2 w-2 rounded-full bg-emerald-500" />
                      TPV activo
                    </span>
                  </div>
                </div>
              }
              subtitle={
                <p className="text-xs" style={{ color: 'var(--color-muted-foreground)' }}>
                  Registra ventas rápidas desde tu catálogo
                </p>
              }
            >
              {collapseMenuBtn}
            </PanelHeader>

            <div className="w-full px-4 py-4 pb-24 sm:px-5 md:px-6 lg:px-8 lg:pb-8">
              <div className="grid w-full gap-4 lg:grid-cols-[minmax(0,65fr)_minmax(340px,35fr)] lg:items-start xl:gap-5">

                {/* ── LEFT: search + categories + products ──
                    Con carrito no vacío, en mobile pasa después del carrito
                    (order-2) para que el carrito sea lo primero visible sin
                    duplicar JSX; en desktop el orden visual no cambia
                    (lg:order-1) porque el layout de 2 columnas ya lo deja
                    visible en la columna derecha sticky.                    */}
                <div className={`min-w-0 flex flex-col gap-2.5 ${cart.length > 0 ? 'order-2' : 'order-1'} lg:order-1`}>

                  {/* ── Panel de diagnóstico — solo en entorno de desarrollo ── */}
                  {import.meta.env.DEV && diagVisible && (
                    <div className="rounded-xl border border-purple-200 bg-purple-50 px-4 py-3 text-xs font-mono space-y-1.5">
                      <div className="flex items-center gap-2 mb-1">
                        <Icon name="Bug" size={13} color="#7c3aed" />
                        <span className="font-bold text-purple-800 text-[11px] not-italic" style={{ fontFamily: 'var(--font-caption)' }}>Diagnóstico TPV</span>
                        {posFallback && (
                          <span className="ml-auto text-[10px] bg-amber-100 text-amber-700 border border-amber-200 px-2 py-0.5 rounded-full font-semibold">
                            ⚠️ Migración POS no aplicada — modo fallback
                          </span>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-purple-700">
                        <span>Productos activos (total):</span>
                        <strong>{allProducts.length}</strong>
                        <span>Productos visibles TPV:</span>
                        <strong>{posProducts.length}{posFallback ? ' (todos — sin filtro show_in_pos)' : ''}</strong>
                        <span>Resultados búsqueda actual:</span>
                        <strong>{isSearching ? filtered.length : '—  (sin búsqueda activa)'}</strong>
                        <span>Búsqueda activa:</span>
                        <strong>{debouncedSearch || '(vacía)'}</strong>
                        <span>Pool de búsqueda:</span>
                        <strong>{allProducts.length > 0 ? `allProducts (${allProducts.length})` : `posProducts fallback (${posProducts.length})`}</strong>
                      </div>
                      {allProducts.length > 0 && (
                        <details className="mt-1">
                          <summary className="cursor-pointer text-purple-600 hover:text-purple-800">Ver primeros 5 productos</summary>
                          <ul className="mt-1 space-y-0.5 pl-2 border-l-2 border-purple-200">
                            {allProducts.slice(0, 5).map(p => (
                              <li key={p.id} className="text-purple-700">
                                <strong>{p.name}</strong>
                                {' · '}sku:{p.sku || '—'}{' · '}code:{p.public_code || '—'}{' · '}show_in_pos:{String(p.show_in_pos ?? '(no field)')}
                              </li>
                            ))}
                          </ul>
                        </details>
                      )}
                    </div>
                  )}

                  {/* TPV-CORE-2: borrador restaurado / pendiente de decisión */}
                  {draftNotice === 'restored' && (
                    <div className="flex items-center gap-2 p-3 rounded-xl bg-blue-50 border border-blue-100 text-sm text-blue-700">
                      <Icon name="History" size={15} color="currentColor" className="shrink-0" />
                      <span>Venta en curso recuperada.</span>
                      <button onClick={() => setDraftNotice(null)} className="ml-auto shrink-0 text-blue-400 hover:text-blue-600">
                        <Icon name="X" size={14} color="currentColor" />
                      </button>
                    </div>
                  )}
                  {draftNotice?.pending && (
                    <div className="flex flex-col gap-2 p-4 rounded-xl bg-amber-50 border border-amber-200 text-sm text-amber-800">
                      <div className="flex items-start gap-2">
                        <Icon name="AlertTriangle" size={16} color="currentColor" className="shrink-0 mt-0.5" />
                        <span className="font-semibold">
                          Tienes una venta pendiente del {new Date(draftNotice.pending.savedAt).toLocaleDateString('es-CL')}.
                        </span>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={handleRecoverStaleDraft}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold transition-colors"
                        >
                          Recuperar venta
                        </button>
                        <button
                          onClick={handleDiscardStaleDraft}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-amber-300 bg-white hover:bg-amber-100 text-amber-700 text-xs font-semibold transition-colors"
                        >
                          Descartar
                        </button>
                      </div>
                    </div>
                  )}
                  {customerRemovedNotice && (
                    <div className="flex items-center gap-2 p-3 rounded-xl bg-amber-50 border border-amber-200 text-sm text-amber-800">
                      <Icon name="UserX" size={15} color="currentColor" className="shrink-0" />
                      <span>El cliente de esta venta ya no está disponible.</span>
                      <button onClick={() => setCustomerRemovedNotice(false)} className="ml-auto shrink-0 text-amber-400 hover:text-amber-600">
                        <Icon name="X" size={14} color="currentColor" />
                      </button>
                    </div>
                  )}

                  {/* Error banner */}
                  {errorMsg === 'NO_OPEN_CASH' ? (
                    <div className="flex flex-col gap-2 p-4 rounded-xl bg-amber-50 border border-amber-200 text-sm text-amber-800">
                      <div className="flex items-start gap-2">
                        <Icon name="AlertTriangle" size={16} color="currentColor" className="shrink-0 mt-0.5" />
                        <span className="font-semibold">Debes abrir caja antes de registrar una venta.</span>
                        <button onClick={() => setErrorMsg(null)} className="ml-auto shrink-0 text-amber-400 hover:text-amber-600">
                          <Icon name="X" size={14} color="currentColor" />
                        </button>
                      </div>
                      <button
                        onClick={() => navigate('/crm/caja')}
                        className="self-start flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold transition-colors"
                      >
                        <Icon name="Landmark" size={13} color="currentColor" />
                        Ir a Caja diaria
                      </button>
                    </div>
                  ) : errorMsg === 'CREDIT_NO_CUSTOMER' ? (
                    <div className="flex flex-col gap-2 p-4 rounded-xl bg-amber-50 border border-amber-200 text-sm text-amber-800">
                      <div className="flex items-start gap-2">
                        <Icon name="AlertTriangle" size={16} color="currentColor" className="shrink-0 mt-0.5" />
                        <span className="font-semibold">Las ventas en cuenta corriente requieren un cliente registrado.</span>
                        <button onClick={() => setErrorMsg(null)} className="ml-auto shrink-0 text-amber-400 hover:text-amber-600">
                          <Icon name="X" size={14} color="currentColor" />
                        </button>
                      </div>
                    </div>
                  ) : errorMsg ? (
                    <div className="flex items-start gap-2 p-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700">
                      <Icon name="AlertCircle" size={16} color="currentColor" className="shrink-0 mt-0.5" />
                      <span>{errorMsg}</span>
                      <button onClick={() => setErrorMsg(null)} className="ml-auto shrink-0 text-red-400 hover:text-red-600">
                        <Icon name="X" size={14} color="currentColor" />
                      </button>
                    </div>
                  ) : null}

                  {/* Search */}
                  <div className="relative rounded-2xl border border-gray-200 bg-white p-2 shadow-sm">
                    <Icon name="Search" size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                    <input
                      ref={searchRef}
                      autoFocus
                      type="text"
                      value={search}
                      onChange={e => setSearch(e.target.value)}
                      onKeyDown={e => {
                        if (e.key !== 'Enter') return;
                        const exact = findExactProduct(search);
                        if (exact) {
                          addToCart(exact);
                          setSearch('');
                          e.preventDefault();
                        } else if (filtered.length === 1) {
                          addToCart(filtered[0]);
                          setSearch('');
                          e.preventDefault();
                        }
                      }}
                      placeholder="Buscar por nombre, SKU o código de barras…"
                      className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm font-medium focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 bg-gray-50 focus:bg-white transition-colors"
                    />
                    {search && (
                      <button
                        onClick={() => setSearch('')}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        <Icon name="X" size={15} />
                      </button>
                    )}
                  </div>

                  <div className="grid gap-2 rounded-2xl border border-gray-200 bg-white p-2 shadow-sm sm:grid-cols-[minmax(0,1fr)_auto_auto]">
                    <div className="flex min-w-0 items-center gap-1.5 rounded-xl border border-gray-200 bg-gray-50 px-2">
                      <Icon name="UserRound" size={14} className="text-gray-400" />
                      <select
                        value={customerId}
                        onChange={e => setCustomerId(e.target.value)}
                        className="min-w-0 flex-1 bg-transparent py-2.5 text-xs font-semibold text-gray-700 focus:outline-none"
                      >
                        <option value="">Consumidor final</option>
                        {customersDisplay.map(c => (
                          <option key={c.id} value={c.id}>
                            {c.name}{c.company ? ` - ${c.company}` : ''}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => setShowNewCustomer(true)}
                        title="Nuevo cliente"
                        className="rounded-lg p-1 text-blue-600 hover:bg-blue-50"
                      >
                        <Icon name="UserPlus" size={14} />
                      </button>
                    </div>
                    <button
                      onClick={() => setShowManualModal(true)}
                      className="inline-flex items-center justify-center gap-2 rounded-xl border border-purple-200 bg-purple-50 px-3 py-2.5 text-xs font-bold text-purple-700 transition-colors hover:bg-purple-100"
                    >
                      <Icon name="PenLine" size={14} />
                      Manual
                    </button>
                    <button
                      onClick={() => setSearch('')}
                      className="inline-flex items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-xs font-bold text-gray-600 transition-colors hover:bg-gray-50"
                    >
                      <Icon name="Eraser" size={14} />
                      Limpiar
                    </button>
                  </div>

                  {/* Category chips */}
                  {categories.length > 0 && (
                    <div className="flex gap-2 flex-wrap">
                      <button
                        onClick={() => setActiveCategory('')}
                        className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                          activeCategory === ''
                            ? 'bg-gray-900 text-white border-gray-900'
                            : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'
                        }`}
                      >
                        Todos
                      </button>
                      {categories.map(cat => (
                        <button
                          key={cat}
                          onClick={() => setActiveCategory(cat === activeCategory ? '' : cat)}
                          className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                            activeCategory === cat
                              ? 'bg-gray-900 text-white border-gray-900'
                              : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'
                          }`}
                        >
                          {cat}
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Manual item button */}
                  <button
                    onClick={() => setShowManualModal(true)}
                    className="hidden"
                  >
                    <Icon name="PenLine" size={14} />
                    Artículo manual
                  </button>

                  {/* Indicador de modo búsqueda */}
                  {isSearching && (
                    <div className="flex items-center gap-2 text-xs text-blue-600 bg-blue-50 border border-blue-100 rounded-lg px-3 py-1.5">
                      <Icon name="Search" size={12} />
                      <span>Buscando en todos los productos activos</span>
                      <button onClick={() => setSearch('')} className="ml-auto text-blue-400 hover:text-blue-600">
                        <Icon name="X" size={12} />
                      </button>
                    </div>
                  )}

                  {/* Product grid */}
                  {posLoading && !isSearching ? (
                    <div className="text-center py-16 text-gray-300 text-sm">
                      <Icon name="Loader2" size={28} className="mx-auto mb-2 animate-spin" />
                      Cargando productos…
                    </div>
                  ) : filtered.length === 0 ? (
                    <div className="text-center py-14 text-gray-400 text-sm">
                      <Icon name="PackageSearch" size={36} className="mx-auto mb-3 text-gray-200" />
                      {isSearching
                        ? `Sin resultados para "${debouncedSearch}"`
                        : posProducts.length === 0
                          ? (
                            <div className="flex flex-col items-center gap-3 py-4">
                              <Icon name="ShoppingBag" size={44} className="text-gray-200" />
                              <div className="space-y-1 text-center">
                                <p className="font-semibold text-gray-700 text-base">No tienes productos visibles en el TPV</p>
                                {allProducts.length > 0 ? (
                                  <p className="text-sm text-gray-400">
                                    Tienes <strong className="text-gray-600">{allProducts.length} producto{allProducts.length !== 1 ? 's' : ''}</strong> registrado{allProducts.length !== 1 ? 's' : ''}, pero ninguno está marcado para venderse desde el Terminal de Ventas.
                                  </p>
                                ) : (
                                  <p className="text-sm text-gray-400">Agrega productos a tu catálogo para poder venderlos desde aquí.</p>
                                )}
                              </div>
                              {allProducts.length > 0 && (
                                <div className="flex items-center gap-2 rounded-xl border border-gray-100 bg-gray-50 px-4 py-2.5 text-xs text-gray-500">
                                  <span>Productos cargados:</span>
                                  <strong className="text-gray-700">{allProducts.length}</strong>
                                  <span className="mx-1 text-gray-300">·</span>
                                  <span>Visibles en TPV:</span>
                                  <strong className="text-gray-700">0</strong>
                                </div>
                              )}
                              <button
                                onClick={() => navigate('/product-management')}
                                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold transition-colors"
                              >
                                <Icon name="Package" size={15} />
                                {allProducts.length > 0 ? 'Configurar productos para TPV' : 'Ir a productos'}
                              </button>
                              <p className="text-[11px] text-gray-400 text-center max-w-xs">
                                Abre el editor de cada producto y activa la opción <strong>"Visible en TPV"</strong> para que aparezca aquí.
                              </p>
                            </div>
                          )
                          : 'Sin productos en esta categoría'
                      }
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
                      {filtered.map(p => {
                        // TPV-STOCK-UX-1: stock_actual NULL -> sin control de
                        // stock, no se muestra ningún indicador (sección 3).
                        const outOfStock = p.stock_actual === 0;
                        const hasStockLabel = typeof p.stock_actual === 'number' && p.stock_actual > 0;
                        return (
                          <button
                            key={p.id}
                            onClick={() => { if (!outOfStock) addToCart(p); }}
                            disabled={outOfStock}
                            className={`group flex h-full flex-col gap-2 rounded-2xl border p-2.5 text-left shadow-sm transition-all ${
                              outOfStock
                                ? 'border-gray-100 bg-gray-50 opacity-60 cursor-not-allowed'
                                : 'border-gray-200 bg-white hover:-translate-y-0.5 hover:border-blue-300 hover:shadow-lg active:scale-[0.98]'
                            }`}
                          >
                            <ProductThumb product={p} />
                            <div className="flex min-h-[92px] min-w-0 flex-1 flex-col px-1 pb-1">
                              <div className="min-h-[16px]">
                                {p.category && (
                                  <p className="truncate text-[10px] font-bold uppercase tracking-wide text-gray-400">{p.category}</p>
                                )}
                              </div>
                              <p className="mt-0.5 min-h-[34px] text-sm font-bold leading-snug text-gray-900 line-clamp-2 group-hover:text-blue-700">{p.name}</p>
                              <div className="mt-auto pt-2">
                                <p className="truncate text-base font-black text-blue-600">{fmt(p.price, business?.currency)}</p>
                                <div className="min-h-[15px]">
                                  {outOfStock ? (
                                    <p className="mt-0.5 text-[10px] font-bold uppercase tracking-wide text-red-500">Sin stock</p>
                                  ) : hasStockLabel ? (
                                    <p className="mt-0.5 text-[10px] font-semibold text-gray-400">Stock: {p.stock_actual}</p>
                                  ) : null}
                                </div>
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* ── RIGHT: cart + checkout ──────────────────────────────────
                    Desktop 3-zone flex column (lg:flex lg:flex-col):
                      Zone 1 lg:flex-none        — customer selector
                      Zone 2 lg:flex-1 lg:min-h-0 — cart card; items scroll inside
                      Zone 3 lg:flex-none        — compact checkout controls +
                                                   totals + cobrar (always visible)

                    Key: cart card is a DIRECT flex-1 child of the column.
                    Checkout controls are flex-none BELOW the cart so they
                    never compress it. Compacted to ~280px leaving ~200px+
                    for the cart on 768px screens.

                    Mobile: normal flow; fixed bottom bar handles cobrar.
                    Con carrito no vacío pasa primero (order-1) en mobile,
                    ver nota en la columna izquierda.                       */}
                <div className={`w-full min-w-0 lg:sticky lg:top-4 lg:flex lg:h-[calc(100vh-5rem)] lg:flex-col ${checkoutStep === 'payment' ? 'lg:overflow-y-auto lg:pr-1' : ''} ${cart.length > 0 ? 'order-1' : 'order-2'} lg:order-2`}>

                  {/* ── Zone 1: header ── flex-none ───────────────────────────
                      TPV-CORE-3: en 'sale' prioriza el total + cantidad de
                      artículos, sin adelantar información de pago (aún no
                      hay cobro en curso). En 'payment' muestra el resumen
                      completo (estado, items/pagado/pendiente) y, si
                      corresponde, el cliente seleccionado -- reutilizando
                      selectedCustomer, sin duplicar estado (sección 9).    */}
                  {checkoutStep === 'sale' ? (
                    <div className="rounded-2xl border border-gray-200 bg-white p-3.5 shadow-sm lg:flex-none">
                      <p className="text-[11px] font-black uppercase tracking-wide text-gray-400">Total venta</p>
                      <div className="mt-1 flex items-end justify-between gap-3">
                        <p className="truncate text-2xl font-black tracking-tight text-gray-950 xl:text-3xl">{fmt(total, business?.currency)}</p>
                        <div className="inline-flex items-center gap-1.5 rounded-xl bg-gray-50 px-2.5 py-2">
                          <Icon name="ShoppingCart" size={12} className="text-gray-400" />
                          <span className="text-xs font-bold text-gray-600">{cartCount} {cartCount === 1 ? 'artículo' : 'artículos'}</span>
                        </div>
                      </div>
                    </div>
                  ) : selectedCustomer ? (
                    <div className="flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs text-gray-600 shadow-sm lg:flex-none">
                      <Icon name="UserRound" size={13} className="text-gray-400" />
                      <span className="truncate font-semibold">{selectedCustomer.name}{selectedCustomer.company ? ` · ${selectedCustomer.company}` : ''}</span>
                    </div>
                  ) : null}

                  <div className="hidden">
                    <div className="flex items-center justify-between mb-1.5">
                      <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wide">Cliente</label>
                      <button
                        type="button"
                        onClick={() => setShowNewCustomer(true)}
                        className="flex items-center gap-1 rounded-lg border border-blue-200 bg-blue-50 px-2 py-0.5 text-[11px] font-bold text-blue-600 hover:bg-blue-100"
                      >
                        <Icon name="UserPlus" size={11} />
                        Nuevo cliente
                      </button>
                    </div>
                    <select
                      value={customerId}
                      onChange={e => setCustomerId(e.target.value)}
                      className="w-full border border-gray-200 rounded-lg px-2.5 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50"
                    >
                      <option value="">👤 Consumidor final</option>
                      {customersDisplay.map(c => (
                        <option key={c.id} value={c.id}>
                          {c.name}{c.company ? ` — ${c.company}` : ''}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* ── Zone 2: Cart card ── flex-1 min-h-0 ──────────────────
                      On desktop this is a flex column:
                        header  → flex-none
                        items   → flex-1 min-h-0 overflow-y-auto
                      This is the key change: cart fills ALL available middle
                      space regardless of how many items are in it.             */}
                  <div
                    ref={cartSectionRef}
                    className={`mt-2.5 overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm lg:flex lg:flex-col ${
                                  checkoutStep === 'payment'
                                    ? 'lg:flex-none lg:max-h-[150px]'
                                    : 'lg:flex-1 lg:min-h-[160px]'
                                }`}
                  >

                    {/* Cart header — flex-none */}
                    <div className="flex items-center justify-between border-b border-gray-100 bg-gray-50 px-4 py-3 lg:flex-none">
                      <div className="flex items-center gap-2">
                        <Icon name="ShoppingCart" size={14} className="text-gray-500" />
                        <span className="text-sm font-bold text-gray-700">
                          Carrito
                          {cartCount > 0 && (
                            <span className="ml-1.5 bg-blue-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">{cartCount}</span>
                          )}
                        </span>
                      </div>
                      {cart.length > 0 && (
                        <button
                          onClick={resetForm}
                          className="text-xs text-red-400 hover:text-red-600 flex items-center gap-1"
                        >
                          <Icon name="Trash2" size={11} />Vaciar
                        </button>
                      )}
                    </div>

                    {/* Cart items — flex-1 min-h-0 overflow-y-auto on desktop;
                        max-h cap on mobile so the page doesn't get too long.
                        TPV-CORE-3: en 'payment' se sigue renderizando EL
                        MISMO listado (un único cart.map, sin segunda copia
                        del carrito) -- producto, cantidad, precio unitario
                        y subtotal por línea permanecen visibles mientras se
                        cobra; solo se ocultan los controles de edición
                        (steppers +/-, eliminar, ajuste de stock) para no
                        permitir tocar el carrito a mitad de un cobro y para
                        ganar espacio vertical. El cajero edita cantidades
                        volviendo con "Volver a la venta". */}
                    {cart.length === 0 ? (
                      <div className="px-4 py-10 text-center lg:flex-1 lg:flex lg:flex-col lg:items-center lg:justify-center">
                        <Icon name="ShoppingCart" size={32} className="mx-auto mb-2 text-gray-200" />
                        <p className="text-gray-400 text-sm">Toca un producto para agregarlo</p>
                      </div>
                    ) : (
                      <div className={`divide-y divide-gray-100 overflow-y-auto
                                      lg:flex-1 lg:min-h-0 lg:max-h-none lg:overflow-y-auto
                                      ${checkoutStep === 'payment' ? 'max-h-40' : 'max-h-52'}`}>
                        {cart.map(item => {
                          // TPV-STOCK-UX-1: límite conocido para este producto
                          // (null = sin control de stock) y si esta línea ya
                          // excede el stock disponible más reciente (sección 8:
                          // draft viejo, o venta concurrente de otro cajero).
                          const stockLimit = item.product_id ? stockById.get(item.product_id) : null;
                          const totalForProduct = item.product_id
                            ? (cartQtyByProduct.get(item.product_id) || item.quantity)
                            : item.quantity;
                          const atStockLimit = typeof stockLimit === 'number' && totalForProduct >= stockLimit;
                          const issue = item.product_id ? cartStockIssues.get(item.product_id) : null;
                          const editable = checkoutStep === 'sale';
                          return (
                          <div key={item._key} className={editable ? "px-3 py-2.5 flex flex-col gap-1.5" : "px-3 py-1.5 flex flex-col gap-1"}>
                            <div className="flex items-center gap-2">
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-semibold text-gray-800 truncate leading-snug flex items-center gap-1">
                                {item.product_id == null && <span className="text-[9px] bg-purple-100 text-purple-600 px-1 py-0.5 rounded font-bold shrink-0">M</span>}
                                {!editable && <span className="text-gray-400">{item.quantity}×</span>}
                                {item.name}
                              </p>
                              <p className="text-[11px] text-gray-400 mt-0.5">{fmt(item.unit_price, business?.currency)} c/u</p>
                              {item.note && <p className="text-[10px] text-gray-400 italic truncate">{item.note}</p>}
                            </div>
                            {editable && (
                              <div className="flex items-center gap-1 shrink-0">
                                <button
                                  onClick={() => updateQty(item._key, -1)}
                                  title={item.quantity === 1 ? 'Quitar del carrito' : 'Reducir cantidad'}
                                  className="w-11 h-11 rounded-lg bg-gray-100 hover:bg-red-50 hover:text-red-500 flex items-center justify-center transition-colors text-gray-600"
                                >
                                  {item.quantity === 1
                                    ? <Icon name="Trash2" size={12} color="currentColor" />
                                    : <Icon name="Minus" size={12} color="currentColor" />
                                  }
                                </button>
                                <span className="w-6 text-center text-sm font-bold text-gray-800">{item.quantity}</span>
                                <button
                                  onClick={() => updateQty(item._key, 1)}
                                  disabled={atStockLimit}
                                  title={atStockLimit ? `Sin más stock disponible (${stockLimit})` : 'Aumentar cantidad'}
                                  className={`w-11 h-11 rounded-lg flex items-center justify-center transition-colors ${
                                    atStockLimit
                                      ? 'bg-gray-50 text-gray-300 cursor-not-allowed'
                                      : 'bg-gray-100 hover:bg-gray-200 text-gray-600'
                                  }`}
                                >
                                  <Icon name="Plus" size={12} color="currentColor" />
                                </button>
                              </div>
                            )}
                            <div className="flex items-center gap-1.5 shrink-0">
                              <span className={editable ? "text-sm font-bold text-gray-900 min-w-[52px] text-right" : "text-xs font-bold text-gray-900"}>
                                {fmt(item.unit_price * item.quantity, business?.currency)}
                              </span>
                              {editable && (
                                <button
                                  onClick={() => removeItem(item._key)}
                                  title="Eliminar producto"
                                  aria-label="Eliminar producto del carrito"
                                  className="w-11 h-11 rounded-lg bg-red-50 hover:bg-red-100 flex items-center justify-center transition-colors text-red-400 hover:text-red-600"
                                >
                                  <Icon name="Trash2" size={13} color="currentColor" />
                                </button>
                              )}
                            </div>
                            </div>
                            {editable && issue && (
                              <div className="flex items-center justify-between gap-2 rounded-lg bg-red-50 border border-red-100 px-2 py-1">
                                <p className="text-[10px] font-semibold text-red-600">
                                  Stock disponible: {issue.available} · En carrito: {issue.requested}
                                </p>
                                <div className="flex items-center gap-2 shrink-0">
                                  <button
                                    type="button"
                                    onClick={() => setCartQuantityTo(item._key, issue.available)}
                                    className="text-[10px] font-bold text-red-700 underline"
                                  >
                                    Ajustar a {issue.available}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => removeItem(item._key)}
                                    className="text-[10px] font-bold text-red-700 underline"
                                  >
                                    Eliminar
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                          );
                        })}
                        {checkoutStep === 'payment' && discountAmount > 0 && (
                          <div className="px-3 py-1.5 flex items-center justify-between text-xs font-semibold text-red-500">
                            <span>Descuento</span>
                            <span>−{fmt(discountAmount, business?.currency)}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* ── Zone 3: Checkout controls ── flex-none ───────────────
                      TPV-CORE-3: flujo en dos etapas. En 'sale' solo se
                      muestran descuento/notas + el CTA "Cobrar $X" -- todo
                      el bloque de pagos queda oculto para dejarle el
                      espacio vertical al carrito (Zone 2). Al presionar
                      Cobrar se pasa a 'payment', que reutiliza EXACTAMENTE
                      el mismo JSX/estado de pagos que ya existía (ninguna
                      lógica de pagos se reimplementa, solo se reordena su
                      visibilidad) y agrega "Volver a la venta", que jamás
                      borra carrito/cliente/descuento/notas/pagos/key/draft.*/}
                  <div className={`flex flex-col gap-2 mt-2.5 lg:flex-none lg:shrink-0 ${checkoutStep === 'payment' ? 'lg:pb-2' : ''}`}>

                    {checkoutStep === 'sale' && (
                      <>
                        {/* Discount + Notes only become relevant once a sale exists. */}
                        {cart.length > 0 && (
                        <div className="flex gap-2">
                          <div className="flex-1 relative">
                            <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs pointer-events-none">$</span>
                            <input
                              type="text"
                              inputMode="numeric"
                              value={fmtMoneyInput(discount)}
                              onChange={e => setDiscount(e.target.value.replace(/\D/g, ''))}
                              placeholder="Descuento"
                              className="w-full pl-6 pr-2 py-2 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                            />
                          </div>
                          <input
                            type="text"
                            value={notes}
                            onChange={e => setNotes(e.target.value)}
                            placeholder="Notas…"
                            className="flex-1 px-2.5 py-2 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                          />
                        </div>
                        )}

                        {/* CTA "Cobrar" — desktop only; mobile usa la barra fija
                            para no duplicar el mismo CTA dos veces en pantalla. */}
                        {cart.length === 0 && heldSales.length > 0 && (
                          <button
                            type="button"
                            onClick={() => setHeldSalesOpen(true)}
                            className="hidden lg:flex w-full items-center justify-between rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-left transition-colors hover:bg-amber-100"
                          >
                            <span>
                              <span className="block text-sm font-bold text-amber-900">Ventas en espera</span>
                              <span className="block text-xs text-amber-700">Recupera una venta para continuar cobrando.</span>
                            </span>
                            <span className="rounded-full bg-amber-200 px-2.5 py-1 text-xs font-black text-amber-900">{heldSales.length}</span>
                          </button>
                        )}

                        {cart.length > 0 && (
                        <div className="hidden lg:flex flex-col gap-2.5 rounded-2xl bg-gray-950 px-4 py-4 shadow-xl">
                          <div className="flex justify-between items-end">
                            <span className="text-sm font-bold text-gray-300">Total</span>
                            <span className="truncate text-2xl font-black tracking-tight text-white xl:text-3xl">{fmt(total, business?.currency)}</span>
                          </div>
                          {hasStockIssues && (
                            <p className="text-center text-xs font-semibold text-red-400">
                              Revisa {cartStockIssues.size} producto{cartStockIssues.size === 1 ? '' : 's'} sin stock suficiente.
                            </p>
                          )}
                          <button
                            type="button"
                            onClick={handleHoldSale}
                            disabled={cart.length === 0 || heldBusy}
                            className="flex w-full items-center justify-center gap-2 rounded-2xl border border-gray-700 px-3 py-3 text-sm font-bold text-gray-200 hover:bg-gray-900 disabled:opacity-40"
                            title="Guardar esta venta y comenzar otra"
                          >
                            <Icon name="Pause" size={16} />
                            Dejar en espera
                          </button>
                          <div className="grid grid-cols-1 gap-2">
                            <button
                              onClick={handleGoToPayment}
                              disabled={cart.length === 0 || hasStockIssues}
                              className="flex w-full items-center justify-center gap-2 rounded-2xl bg-emerald-500 py-3.5 text-base font-black text-white shadow-lg shadow-emerald-950/30 transition-all hover:-translate-y-0.5 hover:bg-emerald-400 disabled:translate-y-0 disabled:bg-gray-800 disabled:text-gray-500 disabled:shadow-none xl:py-4 xl:text-lg min-h-[44px]"
                            >
                              <Icon name="Wallet" size={18} />
                              Cobrar {fmt(total, business?.currency)}
                            </button>
                          </div>
                          <button
                            type="button"
                            onClick={() => setHeldSalesOpen(true)}
                            className="w-full text-center text-xs font-bold text-gray-400 hover:text-white"
                          >
                            Ventas en espera ({heldSales.length})
                          </button>
                        </div>
                        )}
                      </>
                    )}

                    {checkoutStep === 'payment' && (
                      <>
                        {/* POINT-SMART-2-7 — Point integrado. Separado de
                            "Mercado Pago manual": en esta fase cobra el total
                            completo y el backend crea la venta solo al recibir
                            processed desde Mercado Pago. */}
                        <div className="rounded-2xl border border-yellow-200 bg-yellow-50 p-3 space-y-2">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex items-center gap-2">
                              <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-yellow-300">
                                <Icon name="Smartphone" size={16} className="text-gray-900" />
                              </span>
                              <div>
                                <p className="text-xs font-black text-gray-900">Mercado Pago Point</p>
                                <p className="text-[10px] text-gray-600">Cobro integrado · total {fmt(total, business?.currency)}</p>
                              </div>
                            </div>
                            <button type="button" onClick={loadPointTerminals} disabled={pointLoading || !!pointOperation?.operation_id}
                              className="rounded-lg px-2 py-1 text-[10px] font-bold text-gray-700 hover:bg-yellow-100 disabled:opacity-40">
                              Actualizar
                            </button>
                          </div>

                          {!pointOperation?.operation_id ? (
                            <>
                              {pointTerminals.length > 0 ? (
                                <div className="space-y-2">
                                  {pointPreference?.terminal_id && (
                                    <div className="rounded-xl border border-gray-900/10 bg-white px-3 py-2.5">
                                      <div className="flex items-center justify-between gap-2">
                                        <div className="min-w-0">
                                          <p className="text-[10px] font-bold uppercase tracking-wide text-gray-500">Point actual</p>
                                          <p className="break-all text-[11px] font-black text-gray-900">{pointPreference.terminal_id}</p>
                                          <p className="mt-1 text-[10px] text-gray-600">
                                            {pointPreference.verification_status === 'verified'
                                              ? 'Verificada para cobros integrados'
                                              : 'Pendiente de verificación física'}
                                          </p>
                                        </div>
                                        <button type="button" onClick={handleUnlinkPointTerminal} disabled={pointLoading}
                                          className="shrink-0 rounded-lg border border-gray-200 px-2 py-1 text-[10px] font-bold text-gray-700 hover:bg-gray-50 disabled:opacity-40">
                                          Desvincular
                                        </button>
                                      </div>
                                    </div>
                                  )}
                                  {!pointPreference?.terminal_id && (
                                    <p className="text-[10px] font-semibold text-gray-600">
                                      Elige la Point física que usará Walinka. No seleccionaremos una automáticamente.
                                    </p>
                                  )}
                                  <div className="space-y-1.5">
                                    {pointTerminals.map((terminal) => {
                                      const selected = terminal.id === pointPreference?.terminal_id;
                                      const mode = terminal.operating_mode || 'UNDEFINED';
                                      return (
                                        <div key={terminal.id}
                                          className={`w-full rounded-xl border px-3 py-2.5 text-left ${
                                            selected ? 'border-gray-900 bg-white ring-2 ring-gray-900/10' : 'border-yellow-200 bg-white/70'
                                          }`}>
                                          <div className="flex items-start justify-between gap-2">
                                            <div className="min-w-0">
                                              <p className="break-all text-[11px] font-black text-gray-900">{terminal.id}</p>
                                              <p className="mt-1 text-[10px] text-gray-600">
                                                POS {terminal.pos_id || 'sin asignar'} · Tienda {terminal.store_id || 'sin asignar'}
                                              </p>
                                            </div>
                                            <span className={`shrink-0 rounded-full px-2 py-1 text-[9px] font-black ${
                                              mode === 'PDV' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                                            }`}>{mode}</span>
                                          </div>
                                          <button type="button" onClick={() => handleSelectPointTerminal(terminal.id)}
                                            disabled={pointLoading || selected}
                                            className="mt-2 rounded-lg border border-gray-200 px-2.5 py-1.5 text-[10px] font-bold text-gray-800 hover:bg-gray-50 disabled:opacity-50">
                                            {selected ? '✓ Point actual' : pointPreference?.terminal_id ? 'Cambiar a esta Point' : 'Usar esta Point'}
                                          </button>
                                        </div>
                                      );
                                    })}
                                  </div>
                                  {pointTerminalId && pointTerminals.find(t => t.id === pointTerminalId)?.operating_mode !== 'PDV' && (
                                    <button type="button" onClick={handleSetupPoint} disabled={pointLoading}
                                      className="w-full rounded-xl bg-gray-900 px-3 py-2.5 text-xs font-bold text-white disabled:opacity-50">
                                      Activar PDV en la Point actual
                                    </button>
                                  )}
                                  {pointTerminalId &&
                                    pointTerminals.find(t => t.id === pointTerminalId)?.operating_mode === 'PDV' &&
                                    pointPreference?.verification_status !== 'verified' && (
                                    <div className="rounded-xl border border-amber-200 bg-white/80 p-3 space-y-2">
                                      <p className="text-[11px] font-bold text-gray-800">
                                        Confirma en la Point que muestra “Tu point está vinculado al punto de venta”.
                                      </p>
                                      <p className="text-[10px] text-gray-500">
                                        Walinka volverá a comprobar con Mercado Pago que esta misma terminal sigue en modo PDV antes de habilitar cobros.
                                      </p>
                                      <button type="button" onClick={handleVerifyPointTerminal} disabled={pointLoading}
                                        className="w-full rounded-xl bg-gray-900 px-3 py-2.5 text-xs font-black text-white disabled:opacity-50">
                                        Ya está vinculada · verificar
                                      </button>
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <p className="text-[11px] text-gray-600">
                                  {pointLoading ? 'Buscando terminales Point…' : 'No encontramos una terminal Point en la cuenta conectada.'}
                                </p>
                              )}
                              <button type="button" onClick={handlePointCharge}
                                disabled={pointLoading || !pointTerminalId || pointPreference?.verification_status !== 'verified' || pointTerminals.find(t => t.id === pointTerminalId)?.operating_mode !== 'PDV' || total <= 0}
                                className="flex w-full items-center justify-center gap-2 rounded-xl bg-yellow-300 px-3 py-2.5 text-sm font-black text-gray-950 hover:bg-yellow-200 disabled:bg-yellow-100 disabled:text-gray-400">
                                {pointLoading ? <Icon name="Loader2" size={15} className="animate-spin" /> : <Icon name="Zap" size={15} />}
                                Enviar {fmt(total, business?.currency)} a la Point
                              </button>
                            </>
                          ) : (
                            <div className="rounded-xl bg-white border border-yellow-200 p-3 space-y-2">
                              <div className="flex items-center gap-2">
                                <Icon name={pointOperation.status === 'processed' ? 'BadgeCheck' : 'Loader2'} size={16}
                                  className={pointOperation.status === 'processed' ? 'text-emerald-600' : 'animate-spin text-yellow-600'} />
                                <div>
                                  <p className="text-xs font-black text-gray-900">
                                    {pointOperation.status === 'recovering' ? 'Recuperando cobro…'
                                      : pointOperation.status === 'at_terminal' ? 'Esperando al cliente en la Point…'
                                      : pointOperation.status === 'action_required' ? 'Revisa la pantalla de la Point'
                                      : pointOperation.status === 'processed' ? 'Pago aprobado · registrando venta…'
                                      : 'Enviando cobro a la Point…'}
                                  </p>
                                  <p className="text-[10px] text-gray-500">Estado: {pointOperation.status || 'consultando'}</p>
                                </div>
                              </div>
                              <button type="button" onClick={handleCancelPoint} disabled={pointCanceling || pointOperation.status === 'processed'}
                                className="w-full rounded-lg border border-gray-200 px-2 py-2 text-xs font-bold text-gray-600 hover:bg-gray-50 disabled:opacity-40">
                                {pointCanceling ? 'Cancelando…' : 'Cancelar cobro'}
                              </button>
                            </div>
                          )}
                          {!pointOperation?.operation_id && pointLastResult && (
                            <div className="rounded-xl border border-gray-200 bg-white/80 px-3 py-2.5">
                              <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                  <p className="text-[10px] font-bold uppercase tracking-wide text-gray-400">Último cobro Point</p>
                                  <p className="text-xs font-black text-gray-800">Estado: {pointLastResult.status}</p>
                                  {pointLastResult.payment_status_detail && (
                                    <p className="mt-0.5 text-[10px] text-gray-500">Detalle: {pointLastResult.payment_status_detail}</p>
                                  )}
                                  {pointLastResult.order_id && (
                                    <p className="mt-0.5 break-all text-[9px] text-gray-400">Order: {pointLastResult.order_id}</p>
                                  )}
                                </div>
                                <button type="button" onClick={() => setPointLastResult(null)}
                                  className="shrink-0 rounded-lg border border-gray-200 px-2 py-1 text-[10px] font-bold text-gray-500 hover:bg-gray-50">
                                  Ocultar
                                </button>
                              </div>
                            </div>
                          )}
                          {pointConnection && !pointOperation?.operation_id && (
                            <div className="rounded-xl border border-gray-200 bg-white/70 px-3 py-2.5 flex items-center justify-between gap-3">
                              <div className="min-w-0">
                                <p className="text-[10px] font-bold uppercase tracking-wide text-gray-400">Cuenta Point conectada</p>
                                <p className="text-xs font-black text-gray-800 truncate">{pointConnection.provider_user_id || 'Mercado Pago'}</p>
                                <p className="text-[10px] text-gray-500">Solo afecta cobros Point · no Checkout Pro</p>
                              </div>
                              <button type="button" onClick={handleDisconnectPointConnection} disabled={pointLoading}
                                className="shrink-0 rounded-lg border border-red-200 bg-white px-2.5 py-2 text-[11px] font-bold text-red-600 hover:bg-red-50 disabled:opacity-40">
                                Cambiar cuenta
                              </button>
                            </div>
                          )}
                          {pointErrorReason === 'MP_POINT_NOT_CONNECTED' && !pointOperation?.operation_id && (
                            <button type="button" onClick={handleConnectPoint} disabled={pointLoading}
                              className="w-full rounded-xl bg-gray-900 px-3 py-2.5 text-xs font-black text-white hover:bg-gray-800 disabled:opacity-50">
                              Conectar Mercado Pago Point
                            </button>
                          )}
                          {pointError && <p className="rounded-lg bg-white/70 px-2.5 py-2 text-[11px] font-semibold text-red-600">{pointError}</p>}
                        </div>

                        {/* Payment method — reutiliza exactamente el mismo
                            estado/lógica de payments/cuenta corriente que
                            existía antes de TPV-CORE-3; solo cambió CUÁNDO
                            se muestra, no CÓMO funciona. */}
                        <div className="rounded-2xl border border-gray-200 bg-white p-3 space-y-3">
                          <div>
                            <p className="text-[11px] font-bold uppercase tracking-wide text-gray-400">Total a cobrar</p>
                            <p className="text-3xl font-black tracking-tight text-gray-950">{fmt(total, business?.currency)}</p>
                          </div>

                          <div>
                            <p className="mb-2 text-xs font-bold text-gray-700">Medio de pago</p>
                            <div className="grid grid-cols-2 gap-2 xl:grid-cols-3">
                              {REAL_PAYMENT_METHODS.map((method) => {
                                const selected = payments.length === 1 && payments[0]?.method === method.value && parseMoneyInput(payments[0]?.amount) > 0;
                                return (
                                  <button
                                    key={method.value}
                                    type="button"
                                    onClick={() => selectQuickPaymentMethod(method.value)}
                                    className={`flex min-h-[54px] items-center gap-2 rounded-xl border px-3 py-2.5 text-left transition-colors ${selected ? 'border-gray-950 bg-gray-950 text-white' : 'border-gray-200 bg-white text-gray-800 hover:border-gray-400 hover:bg-gray-50'}`}
                                  >
                                    <Icon name={method.icon} size={17} />
                                    <span className="flex-1 text-center text-[11px] font-black leading-tight xl:text-xs">{method.value === 'bank_transfer' ? 'Transfer.' : method.label}</span>
                                  </button>
                                );
                              })}
                            </div>
                          </div>

                          {payments.length === 1 && payments[0]?.method === 'cash' && (
                            <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3">
                              <label className="mb-1.5 block text-xs font-bold text-emerald-900">Efectivo recibido</label>
                              <div className="relative">
                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-bold text-emerald-700">$</span>
                                <input
                                  type="text"
                                  inputMode="numeric"
                                  value={fmtMoneyInput(payments[0].amount)}
                                  onChange={(e) => updatePayment(payments[0].id, { amount: e.target.value.replace(/\D/g, '') })}
                                  className="w-full rounded-xl border border-emerald-200 bg-white py-3 pl-7 pr-3 text-lg font-black text-gray-950 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                  aria-label="Efectivo recibido"
                                />
                              </div>
                              <div className="mt-2 flex items-center justify-between text-xs">
                                <span className="font-semibold text-emerald-800">Vuelto</span>
                                <span className="text-base font-black text-emerald-950">{fmt(change, business?.currency)}</span>
                              </div>
                            </div>
                          )}

                          {!splitPaymentMode && payments.length === 1 && (
                            <button
                              type="button"
                              onClick={startSplitPayment}
                              className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-left text-xs font-bold text-blue-700 hover:bg-blue-50"
                            >
                              <span className="flex items-center justify-between gap-2">
                                <span className="flex items-center gap-2">
                                  <Icon name="Split" size={14} />
                                  Dividir pago
                                </span>
                                <span className="text-[10px] font-semibold text-gray-500">2 o más medios</span>
                              </span>
                            </button>
                          )}

                          {splitPaymentMode ? (
                            <div className="rounded-xl border border-blue-200 bg-blue-50/40 p-3 space-y-2.5">
                              <div className="flex items-center justify-between gap-2">
                                <div>
                                  <p className="text-xs font-black text-gray-800">Pago dividido</p>
                                  <p className="text-[10px] font-semibold text-gray-500">Ingresa el primer monto; Walinka completa el saldo restante.</p>
                                </div>
                                <button
                                  type="button"
                                  onClick={addPaymentForRemainingBalance}
                                  disabled={pendingBalance <= 0}
                                  className="shrink-0 h-8 px-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white text-xs font-bold flex items-center gap-1"
                                >
                                  <Icon name="Plus" size={13} />
                                  Agregar saldo
                                </button>
                              </div>

                              <div className="space-y-2">
                                {payments.map((payment, index) => (
                                  <div key={payment.id} className="rounded-xl border border-gray-200 bg-white p-2">
                                    <div className="mb-1.5 flex items-center justify-between">
                                      <span className="text-[10px] font-black uppercase tracking-wide text-gray-400">Pago {index + 1}</span>
                                      {payments.length > 1 && (
                                        <button
                                          type="button"
                                          onClick={() => removePayment(payment.id)}
                                          className="text-[10px] font-bold text-red-500 hover:text-red-700"
                                        >
                                          Quitar
                                        </button>
                                      )}
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <select
                                        value={payment.method}
                                        onChange={(e) => updatePayment(payment.id, { method: e.target.value })}
                                        className="w-[118px] rounded-lg border border-gray-200 bg-gray-50 px-2 py-2.5 text-xs font-bold focus:outline-none focus:ring-2 focus:ring-blue-500"
                                      >
                                        {REAL_PAYMENT_METHODS.map((method) => (
                                          <option key={method.value} value={method.value}>{method.label}</option>
                                        ))}
                                      </select>
                                      <div className="relative min-w-0 flex-1">
                                        <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs">$</span>
                                        <input
                                          type="text"
                                          inputMode="numeric"
                                          value={fmtMoneyInput(payment.amount)}
                                          onChange={(e) => updatePayment(payment.id, { amount: e.target.value.replace(/\D/g, '') })}
                                          placeholder={index === 0 ? 'Primer monto' : 'Monto'}
                                          className="w-full rounded-lg border border-gray-200 bg-gray-50 py-2.5 pl-6 pr-2 text-sm font-black focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>

                              <div className="flex items-center justify-between rounded-lg bg-white px-3 py-2">
                                <span className="text-xs font-bold text-gray-600">Falta por asignar</span>
                                <span className={`text-sm font-black ${pendingBalance > 0 ? 'text-amber-700' : 'text-emerald-700'}`}>{fmt(pendingBalance, business?.currency)}</span>
                              </div>
                            </div>
                          ) : null}

                          <button
                            type="button"
                            onClick={() => setPayments(prev => prev.map(p => ({ ...p, amount: '' })))}
                            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl border border-dashed border-amber-300 bg-amber-50 hover:bg-amber-100 transition-colors text-left"
                          >
                            <span className="w-7 h-7 rounded-lg bg-amber-100 flex items-center justify-center shrink-0">
                              <Icon name="BookUser" size={14} className="text-amber-600" />
                            </span>
                            <span className="min-w-0">
                              <span className="block text-xs font-bold text-amber-800 leading-tight">Vender a cuenta corriente</span>
                              <span className="block text-[10px] text-amber-600 leading-tight mt-0.5">Deja el total pendiente · requiere cliente registrado</span>
                            </span>
                          </button>

                          <div className="grid grid-cols-3 gap-1.5">
                            <div className="rounded-lg bg-emerald-50 px-2 py-1.5">
                              <p className="text-[10px] text-emerald-700 font-semibold">Pagado</p>
                              <p className="text-xs font-bold text-emerald-800">{fmt(paidTotal, business?.currency)}</p>
                            </div>
                            <div className={`rounded-lg px-2 py-1.5 ${pendingBalance > 0 ? 'bg-amber-50' : 'bg-gray-50'}`}>
                              <p className={`text-[10px] font-semibold ${pendingBalance > 0 ? 'text-amber-700' : 'text-gray-500'}`}>Pendiente</p>
                              <p className={`text-xs font-bold ${pendingBalance > 0 ? 'text-amber-800' : 'text-gray-700'}`}>{fmt(pendingBalance, business?.currency)}</p>
                            </div>
                            <div className={`rounded-lg px-2 py-1.5 ${change > 0 ? 'bg-blue-50' : 'bg-gray-50'}`}>
                              <p className={`text-[10px] font-semibold ${change > 0 ? 'text-blue-700' : 'text-gray-500'}`}>Vuelto</p>
                              <p className={`text-xs font-bold ${change > 0 ? 'text-blue-800' : 'text-gray-700'}`}>{fmt(change, business?.currency)}</p>
                            </div>
                          </div>

                          {hasNonCashOverpay && (
                            <p className="text-xs font-semibold text-red-600">Solo efectivo puede generar vuelto.</p>
                          )}
                          {requiresCustomerForPending && !customerId && (
                            <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5">
                              <p className="text-xs font-semibold text-amber-800 leading-snug">
                                Faltan <strong>{fmt(pendingBalance, business?.currency)}</strong> por pagar. Completa el pago o selecciona un cliente para dejar saldo en cuenta corriente.
                              </p>
                            </div>
                          )}
                        </div>

                        {/* Pago recibido — solo efectivo */}
                        {/* Totals + Confirmar venta — desktop only; mobile uses fixed bar */}
                        <div className="hidden lg:flex flex-col gap-2.5 rounded-2xl bg-gray-950 px-4 py-4 shadow-xl">
                          {discountAmount > 0 && (
                            <div className="flex justify-between text-xs text-gray-400">
                              <span>Descuento aplicado</span>
                              <span className="font-bold text-red-400">-{fmt(discountAmount, business?.currency)}</span>
                            </div>
                          )}
                          {requiresCustomerForPending && !customerId ? (
                            <div className="flex gap-2">
                              <button
                                disabled
                                className="flex-1 flex items-center justify-center gap-2 rounded-2xl bg-gray-800 py-3.5 text-sm font-black text-gray-500 shadow-none cursor-not-allowed xl:py-4"
                              >
                                <Icon name="AlertCircle" size={16} />
                                Falta pagar {fmt(pendingBalance, business?.currency)}
                              </button>
                              <button
                                type="button"
                                onClick={addPayment}
                                className="shrink-0 flex items-center justify-center gap-1.5 rounded-2xl bg-blue-600 hover:bg-blue-700 px-3.5 py-3.5 text-xs font-bold text-white transition-colors xl:py-4"
                                title="Agregar pago"
                              >
                                <Icon name="Plus" size={15} />
                                <span className="hidden xl:inline">Agregar pago</span>
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={handleRegister}
                              disabled={cart.length === 0 || busy || isPaymentInvalid}
                              className="flex w-full items-center justify-center gap-2 rounded-2xl bg-emerald-500 py-3.5 text-base font-black text-white shadow-lg shadow-emerald-950/30 transition-all hover:-translate-y-0.5 hover:bg-emerald-400 disabled:translate-y-0 disabled:bg-gray-800 disabled:text-gray-500 disabled:shadow-none xl:py-4 xl:text-lg"
                            >
                              {busy
                                ? <><Icon name="Loader2" size={18} className="animate-spin" />Registrando…</>
                                : <><Icon name={pendingBalance > 0 ? 'BookUser' : 'Zap'} size={18} />Confirmar venta</>
                              }
                            </button>
                          )}
                          <p className="text-center text-xs text-gray-400">{paymentStatusLabel}</p>
                        </div>

                        {/* Volver a la venta — desktop only; el propio flujo
                            mobile ya ofrece "Volver a la venta" en la barra
                            fija (sección 14), así que este botón no se
                            duplica ahí. Nunca borra carrito/pagos/key/draft. */}
                        <button
                          type="button"
                          onClick={handleBackToSale}
                          className="hidden lg:flex w-full items-center justify-center gap-2 rounded-2xl border border-gray-200 bg-white py-3 text-sm font-bold text-gray-600 hover:bg-gray-50 transition-colors min-h-[44px]"
                        >
                          <Icon name="ArrowLeft" size={16} />
                          Volver a la venta
                        </button>
                      </>
                    )}

                  </div>{/* end zone 3 */}

                </div>{/* end right column */}
              </div>{/* end flex row */}
            </div>{/* end outer padding container */}

            {/* ── Mobile sticky bottom bar ────────────────────────────────────
                Fixed at viewport bottom on small screens (lg:hidden).
                pb-24 on outer container prevents overlap with content.
                TPV-CORE-3: la franja superior se adapta según checkoutStep
                en vez de duplicar JSX del carrito -- en 'sale' es "Ver
                carrito (N)" (scroll a Zone 2, comportamiento de TPV-CORE-2
                sin cambios); en 'payment' se reemplaza por "Volver a la
                venta" en el MISMO lugar, evitando dos CTA distintos a la
                vez. El CTA principal (fila inferior) también cambia:
                "Cobrar" en 'sale', "+Pago"/"Confirmar" en 'payment' (igual
                que antes) -- nunca ambos simultáneamente.                   */}
            <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-gray-900 border-t border-gray-800 shadow-2xl" style={{ zIndex: 150 }}>
              {checkoutStep === 'payment' ? (
                <button
                  type="button"
                  onClick={handleBackToSale}
                  className="w-full flex items-center justify-center gap-1.5 px-3 py-2.5 border-b border-gray-800 text-xs font-bold text-gray-300 hover:text-white transition-colors min-h-[44px]"
                >
                  <Icon name="ArrowLeft" size={13} color="currentColor" />
                  Volver a la venta
                </button>
              ) : cart.length > 0 && (
                <button
                  type="button"
                  onClick={() => cartSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                  className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 border-b border-gray-800 text-xs font-bold text-gray-300 hover:text-white transition-colors"
                >
                  <Icon name="ShoppingCart" size={13} color="currentColor" />
                  Ver carrito ({cartCount})
                </button>
              )}
              <div className="px-3 py-2.5 flex items-center gap-3">
                <div className="flex-1 min-w-0">
                {discountAmount > 0 && (
                  <p className="text-[10px] text-gray-500 line-through leading-none mb-0.5">
                    {fmt(subtotal, business?.currency)}
                  </p>
                )}
                <p className="truncate text-lg font-bold text-white leading-tight">{fmt(total, business?.currency)}</p>
                {checkoutStep === 'payment' ? (
                  <p className={`text-[10px] mt-0.5 ${isPaymentInvalid ? 'text-amber-400' : 'text-gray-400'}`}>
                    {requiresCustomerForPending && !customerId
                      ? `Faltan ${fmt(pendingBalance, business?.currency)} · selecciona cliente`
                      : `Pagado ${fmt(paidTotal, business?.currency)} · Pendiente ${fmt(pendingBalance, business?.currency)}`
                    }
                  </p>
                ) : hasStockIssues && (
                  <p className="text-[10px] mt-0.5 text-red-400">
                    Revisa {cartStockIssues.size} producto{cartStockIssues.size === 1 ? '' : 's'} sin stock suficiente.
                  </p>
                )}
              </div>
              {checkoutStep === 'sale' ? (
                <div className="flex shrink-0 gap-2">
                  {cart.length > 0 ? (
                    <button
                      type="button"
                      onClick={handleHoldSale}
                      disabled={heldBusy}
                      className="px-3 py-3 rounded-xl border border-gray-700 text-gray-200 font-bold text-xs min-h-[44px] disabled:opacity-40"
                    >
                      Dejar en espera
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setHeldSalesOpen(true)}
                      className="px-3 py-3 rounded-xl border border-gray-700 text-gray-200 font-bold text-xs min-h-[44px]"
                    >
                      En espera ({heldSales.length})
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={handleGoToPayment}
                    disabled={cart.length === 0 || hasStockIssues}
                    className="shrink-0 px-4 py-3 rounded-xl bg-emerald-500 hover:bg-emerald-400 disabled:bg-gray-700 disabled:text-gray-500 text-white font-bold text-sm transition-colors flex items-center gap-2 min-h-[44px]"
                  >
                    <Icon name="Wallet" size={16} />
                    Cobrar
                  </button>
                </div>
              ) : requiresCustomerForPending && !customerId ? (
                <button
                  type="button"
                  onClick={addPayment}
                  className="shrink-0 px-4 py-3 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-bold text-sm transition-colors flex items-center gap-2"
                >
                  <Icon name="Plus" size={16} />
                  + Pago
                </button>
              ) : (
                <button
                  onClick={handleRegister}
                  disabled={cart.length === 0 || busy || isPaymentInvalid}
                  className="shrink-0 px-4 py-3 rounded-xl bg-emerald-500 hover:bg-emerald-400 disabled:bg-gray-700 disabled:text-gray-500 text-white font-bold text-sm transition-colors flex items-center gap-2"
                >
                  {busy
                    ? <><Icon name="Loader2" size={16} className="animate-spin" />Procesando…</>
                    : <><Icon name={pendingBalance > 0 ? 'BookUser' : 'Zap'} size={16} />Confirmar</>
                  }
                </button>
              )}
              </div>
            </div>

          </>
        )}

      </main>

      {heldSalesOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-lg rounded-2xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b px-5 py-4">
              <div>
                <h2 className="font-black text-gray-900">Ventas en espera</h2>
                <p className="text-xs text-gray-500">{heldSales.length} pendiente{heldSales.length === 1 ? '' : 's'}</p>
              </div>
              <button type="button" onClick={() => setHeldSalesOpen(false)} className="p-2 text-gray-400 hover:text-gray-700">
                <Icon name="X" size={18} />
              </button>
            </div>
            <div className="max-h-[60vh] space-y-2 overflow-y-auto p-4">
              {heldSales.length === 0 ? (
                <div className="py-10 text-center text-sm text-gray-500">No hay ventas en espera.</div>
              ) : heldSales.map((sale) => (
                <div key={sale.id} className="rounded-xl border border-gray-200 p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-black text-gray-900">
                        {sale.label || sale.customer_name || 'Venta en espera'}
                      </p>
                      <p className="mt-1 text-xs text-gray-500">
                        {sale.unit_count} {Number(sale.unit_count) === 1 ? 'artículo' : 'artículos'} · {fmt(Number(sale.total || 0), business?.currency)}
                      </p>
                      <p className="mt-1 text-[11px] text-gray-400">{new Date(sale.created_at).toLocaleString()}</p>
                    </div>
                    <div className="flex shrink-0 gap-1.5">
                      <button
                        type="button"
                        disabled={heldBusy || cart.length > 0}
                        onClick={() => handleResumeHeldSale(sale.id)}
                        className="rounded-lg bg-gray-950 px-3 py-2 text-xs font-bold text-white disabled:cursor-not-allowed disabled:bg-gray-300 disabled:opacity-100"
                      >
                        Reanudar
                      </button>
                      <button
                        type="button"
                        disabled={heldBusy}
                        onClick={() => handleDiscardHeldSale(sale.id)}
                        className="rounded-lg px-2 py-2 text-xs font-bold text-red-600 hover:bg-red-50 disabled:opacity-40"
                      >
                        Descartar
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {cart.length > 0 && heldSales.length > 0 && (
              <p className="border-t px-4 py-3 text-xs text-amber-700">
                Termina o deja en espera la venta actual antes de reanudar otra.
              </p>
            )}
          </div>
        </div>
      )}

      {/* Manual item modal */}
      {showManualModal && (
        <ManualItemModal
          currency={business?.currency}
          onAdd={addManualItem}
          onClose={() => setShowManualModal(false)}
        />
      )}

      {/* Thermal ticket modal — rendered outside main flow to avoid layout issues */}
      {ticketData && (
        <CrmThermalTicket
          business={business}
          sale={ticketData.sale}
          items={ticketData.items}
          customer={ticketData.customer}
          paymentMethod={ticketData.paymentMethod}
          payments={ticketData.payments}
          discountAmount={ticketData.discountAmount}
          subtotal={ticketData.subtotal}
          total={ticketData.total}
          amountReceived={ticketData.amountReceived}
          change={ticketData.change}
          initialPaymentAmount={ticketData.initialPaymentAmount}
          initialPaymentMethod={ticketData.initialPaymentMethod}
          pendingBalance={ticketData.pendingBalance}
          notes={ticketData.notes}
          createdAt={ticketData.createdAt}
          onNewSale={handleNewSale}
          onClose={handleCloseTicket}
          onReprint={handleReprint}
          printStatus={printStatus}
          printErrorMessage={printErrorMessage}
          onConfigurePrinter={handleGoToPrintSettings}
        />
      )}

      {showNewCustomer && (
        <QuickCustomerModal
          onSave={handleCreateCustomer}
          onClose={() => setShowNewCustomer(false)}
        />
      )}

    </div>
  );
}

export default function CrmTerminal() {
  return (
    <CrmTerminalBoundary>
      <CrmTerminalUI />
    </CrmTerminalBoundary>
  );
}
